import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getCalcVersion, isEntryStale, isMethodStale, staleNotes, misreadValues } from '../calcVersions';
import { isProseOutput } from '../calcOutputLayout';
import { parseNumberInput, numberInputIssue, INPUT_READER_VERSION } from '../numberInput';
import { useI18n } from '../i18n';

/*
 * AquaIQ Calculator Dashboard — engineering reference build
 * RAS design suite for AquaNetica Group Ltd (Aqua IQ).
 *
 * Built for a working aquaculture engineer: every calculator exposes its
 * governing equation so results can be sense-checked. All outputs are
 * indicative estimates only and must be independently verified. Inputs
 * carry typical RAS reference ranges.
 *
 * Layout: left nav | sticky centre form | scrolling worksheet panel.
 *
 * localStorage keys:
 *   aqua-iq-projects      array of project objects ({ id, name, ... })
 *   aqua-iq-active-proj   id of the active project
 *   aqua-iq-calc-results  object keyed by project id -> array of saved results
 */

// Every colour is a theme token (var(--…)) resolved through the app's
// .App.light / .App.dark cascade, so the dashboard follows dark mode and the
// site palette (sage accent, black pill buttons) from one place.
const TEAL = 'var(--accent)';
const TEAL_DARK = 'var(--accent-text)';
const MUTED = 'var(--text-muted)';

/*
 * Read a user-entered number. Separators, and the "5.000" case that means
 * five in English and five thousand in much of Latin America, are resolved
 * in numberInput.js; an ambiguous value reads as NaN here and runCalc tells
 * the user why before computing.
 */
const n = (v) => parseNumberInput(v);
const fmt = (x, dp = 2) => {
  if (!Number.isFinite(x)) return '—';
  const f = Math.pow(10, dp);
  return (Math.round(x * f) / f).toLocaleString('en-GB', {
    maximumFractionDigits: dp,
  });
};

/*
 * A field's label, unit or hint may be given as a function of the current
 * form values. Calculators whose meaning shifts with a mode selector use
 * this to state the basis exactly — Dosing, where alkalinity and calcium
 * are mg/L as CaCO₃ but pH is unitless.
 */
const resolveField = (prop, vals) => (typeof prop === 'function' ? prop(vals) : prop);

/* ------------------------------------------------------------------ */
/* Shared design constants                                             */
/* ------------------------------------------------------------------ */

/*
 * Held in one place so calculators cannot drift apart — System O₂ Demand
 * and Carrying Capacity previously disagreed on oxygen per kg of feed.
 * Source throughout: REVISED 2022 RAS Yellowbook (Timmons & Ebeling).
 */

// Ch.8: "the oxygen demand could be as low as 0.37 kg of oxygen per kg of
// feed fed (0.25 kg for fish metabolism and 0.12 kg for nitrification).
// For design purposes however, using a ratio of 1.0 kg oxygen per kg of
// feed is a safe starting point."
const O2_PER_FEED_DESIGN = 1.0;
// Fish metabolism alone. Only ever used to show the theoretical floor.
const O2_RESP_PER_FEED_FLOOR = 0.25;

// Ch.3, eq. 3.9b commentary: "Rule of Thumb calculation is to take 3% of
// the feeding rate as an estimate of P_TAN."
const TAN_PER_KG_FEED = 0.03;

/*
 * Ch.3 eq. 3.9b: P_TAN = F x PC x 0.092 x (FCR / FCR_REF), where PC is protein
 * content as a decimal. "The constant in the ammonia equation 3.9b is based
 * upon a series of approximations ... 0.092 = 0.16 * 0.80 * 0.80 * 0.90".
 *
 * The FCR correction is omitted here, i.e. FCR is taken as FCR_REF (~1.5),
 * which is the form the book uses in its own Ch.8 worked example.
 *
 * The two methods agree only where 0.03 = PC x 0.092, i.e. PC = 32.6%. Above
 * that the 3% rule under-predicts TAN and so undersizes nitrification — by
 * ~7% at 35% protein, ~27% at 45% and ~35% at 50%, which is exactly the
 * salmonid and marine range.
 */
const TAN_PROTEIN_FACTOR = 0.092;
const TAN_RULE_EQUIV_PROTEIN_PCT = (TAN_PER_KG_FEED / TAN_PROTEIN_FACTOR) * 100; // 32.6%
// Classic nitrification stoichiometry, g O₂ per g TAN oxidised.
const O2_PER_G_TAN = 4.57;

/*
 * Single source of TAN production for every calculator that needs it, so the
 * biofilter, the feed tool and the oxygen floor cannot disagree about how much
 * ammonia a given feed load produces.
 *
 * Protein given -> eq. 3.9b. Blank -> the 3% rule of thumb. Either way the
 * caller gets back a description of which was used, because the choice moves
 * the answer by a third at salmonid protein levels and must not be silent.
 */
const tanFromFeed = (feedKgPerDay, proteinPctRaw) => {
  const protein = n(proteinPctRaw);
  const equiv = fmt(TAN_RULE_EQUIV_PROTEIN_PCT, 1);
  if (Number.isFinite(protein) && protein > 0) {
    return {
      basis: 'eq3.9b',
      tanKg: feedKgPerDay * (protein / 100) * TAN_PROTEIN_FACTOR,
      method: `Eq. 3.9b — ${fmt(feedKgPerDay, 2)} kg feed × ${fmt(protein, 1)}% protein × 0.092`,
      why:
        `Feed protein was given, so TAN is derived from Timmons & Ebeling eq. 3.9b rather than the 3% rule of thumb. ` +
        `The 3% rule is only equivalent at about ${equiv}% protein; above that it under-predicts TAN and undersizes nitrification.`,
    };
  }
  return {
    basis: 'ruleOfThumb',
    tanKg: feedKgPerDay * TAN_PER_KG_FEED,
    method: `3% rule of thumb — ${fmt(feedKgPerDay, 2)} kg feed × 0.03`,
    why:
      `No feed protein given, so the Yellowbook 3% rule of thumb is used. That rule assumes roughly ${equiv}% protein. ` +
      `Enter your feed protein to switch to eq. 3.9b — at 45% protein the 3% rule under-predicts TAN by about 27%, and by about 35% at 50%.`,
  };
};

/* Shared field definition, so the three TAN-driven tools ask identically. */
const FEED_PROTEIN_FIELD = {
  key: 'feedProtein',
  label: 'Feed protein (optional)',
  unit: '%',
  def: '',
  placeholder: 'e.g. 45',
  hint:
    'Leave blank to use the 3% of feed rule of thumb, which assumes roughly 32.6% protein. ' +
    'Typical crude protein: tilapia and catfish 32–38%, trout 38–45%, salmon 42–50%, marine carnivores 45–55%. ' +
    'Entering a value switches to Timmons & Ebeling eq. 3.9b, which matters most at salmonid and marine protein levels.',
};

/*
 * Dosing constants, both on a CaCO₃-equivalent basis so they match the
 * Langelier tool and the way alkalinity and hardness come off a titration.
 * 1 mg/L as CaCO₃ in 1 m³ = 1 g CaCO₃ equivalent = 1/50.04 = 0.01998 eq.
 *   NaHCO₃      eq wt 84.01 → 0.01998 × 84.01 = 1.68 g
 *   CaCl₂·2H₂O  eq wt 73.51 → 0.01998 × 73.51 = 1.47 g
 * Note 3.67 g would be the dose per mg/L of *elemental* Ca, a different
 * basis from everything else in this suite — do not reintroduce it.
 */
const NAHCO3_G_PER_MGL_CACO3_PER_M3 = 1.68;
const CACL2_G_PER_MGL_CACO3_PER_M3 = 1.47;

// Alkalinity consumed per g TAN nitrified, g as CaCO₃.
const ALK_PER_G_TAN = 7.14;

/*
 * Areal TAN removal rates, g TAN/m² of media per day. Ch.8:
 *  - "for coldwater applications (12–15ºC) when TAN concentrations entering
 *    a trickling column are less than 1–2 mg/L, then the Areal TAN removal
 *    rate is only about 0.15–0.25 g TAN/m2 day" — midpoint 0.20 used here.
 *  - 0.45 g/m²/day is the rate used in the Ch.8 worked design example.
 *  - An RBC at 26 °C and TAN 3 mg/L measured 1.2; 0.70 stays conservative.
 * ATR also falls in seawater: "assuming a 30% reduction for saltwater
 * applications is a guideline".
 */
/*
 * Filled volume of a capsule (a cylinder of length L closed by hemispherical
 * ends of radius r) at liquid height h measured from the very bottom.
 *
 * Three regions:
 *   h <= r          liquid is inside the bottom hemisphere: spherical cap
 *   r < h <= r + L  bottom hemisphere full, plus a cylindrical slice
 *   h > r + L       plus a cap of the top hemisphere
 *
 * Spherical cap of height d on a sphere of radius r: π d² (3r − d) / 3.
 * At h = L + 2r this returns πr²L + 4πr³/3, the full capsule volume.
 */
const capsuleFilledVolume = (r, L, h) => {
  if (!(h > 0)) return 0;
  const hemisphere = (2 / 3) * Math.PI * r * r * r;
  const cap = (d) => (Math.PI * d * d * (3 * r - d)) / 3;
  if (h <= r) return cap(h);
  if (h <= r + L) return hemisphere + Math.PI * r * r * (h - r);
  const topCap = Math.min(h - r - L, r);
  return hemisphere + Math.PI * r * r * L + cap(topCap);
};

/*
 * Settling basin surface overflow rate, m/h.
 *
 * Ch.5: "Stechey and Trudell (1990) recommend an overflow rate (Vo) for the
 * design of settling basins in intensive salmonid aquaculture to be between
 * 40–80 m3/m2 per day", i.e. 1.67–3.33 m/h, and those rates "are applicable
 * to off-line settling basins (the most widely used application in RAS)".
 * Mudrak (1981) measured >90% settleable-solids removal at ~60 m3/m2 per day
 * — 2.5 m/h — and found "no notable improvement in removal efficiencies when
 * the loading rate was further reduced by as much as a factor of three", so
 * the previous 0.5–1.5 m/h band oversized basins for no gain.
 */
const SOR_DESIGN_M_PER_H = 2.5;
const SOR_BAND_M_PER_H = [1.67, 3.33];

/* Hydraulics. */
const WATER_DENSITY = 1000; // kg/m³
const KW_PER_HP = 0.746;
const PUMP_EFFICIENCY = 0.75;
/*
 * Standard motor ratings in HP. Motors are not sold in arbitrary sizes, so
 * rounding shaft power up to the next whole number would skip real frames —
 * 1.2 HP needs a 1.5 HP motor, not a 2 HP one.
 */
const MOTOR_FRAMES_HP = [
  0.25, 0.33, 0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100,
];

/*
 * Pipe internal diameter is entered in MILLIMETRES everywhere — that is how
 * pipe is actually specified, and the tool used to be split between mm and
 * metres, so someone entering a 110 mm pipe as 0.11 into a metres field got a
 * plausible-looking answer wrong by a factor of a thousand with nothing
 * flagging it. Formulas that need metres convert with mm/1000 at the point of
 * use.
 *
 * A value outside this band is almost certainly a metres value typed into a mm
 * field: every common aquaculture pipe entered as metres (0.05 to 0.5) lands
 * below 6, and 2000 mm is well above anything in a RAS. Flagged, not blocked,
 * so a genuinely unusual pipe still computes.
 */
const PIPE_ID_MIN_MM = 6;
const PIPE_ID_MAX_MM = 2000;
const pipeDiameterWarning = (mm) => {
  if (!Number.isFinite(mm)) return null;
  if (mm < PIPE_ID_MIN_MM)
    return `${fmt(mm, 3)} mm is smaller than any aquaculture pipe. Enter the internal diameter in millimetres — a 110 mm pipe is 110, not 0.11.`;
  if (mm > PIPE_ID_MAX_MM)
    return `${fmt(mm, 0)} mm is larger than typical aquaculture pipework (up to ${PIPE_ID_MAX_MM} mm). Check the value is an internal diameter in millimetres.`;
  return null;
};

const ATR_COLD = 0.20;
const ATR_MODERATE = 0.45;
const ATR_WARM = 0.70;
const ATR_SALTWATER_DERATE = 0.30;

/* ------------------------------------------------------------------ */
/* Calculator definitions                                              */
/* ------------------------------------------------------------------ */

// Exported for the version guard in calcVersions.guard.test.js, which
// fingerprints every calculator and fails if one changes behaviour without a
// calcVersions.js bump. Not intended for use by other components.
export const CALCS = [
  /* ====================== WATER QUALITY ====================== */
  {
    id: 'unionisedAmmonia',
    group: 'Water Quality',
    label: 'Un-ionised Ammonia',
    blurb: 'Toxic NH₃ fraction of TAN as a function of pH and temperature.',
    method:
      'NH₃-N = TAN × f, where f = 1 / (1 + 10^(pKa − pH)) and pKa = 0.09018 + 2729.92 / T(K). Freshwater model; seawater NH₃ is modestly lower for the same pH and temperature, so this is the conservative case.',
    fields: [
      { key: 'tan', label: 'Total ammonia nitrogen (TAN)', unit: 'mg/L', def: '1.0', hint: 'RAS target generally < 1–3 mg/L' },
      { key: 'ph', label: 'pH', unit: '', def: '7.4', hint: 'RAS culture range typically 6.5–7.5' },
      { key: 'temp', label: 'Temperature', unit: '°C', def: '25', hint: 'Use culture temperature' },
    ],
    compute: (v) => {
      const tan = n(v.tan);
      const ph = n(v.ph);
      const t = n(v.temp);
      if (!(tan >= 0)) return { error: 'Enter a valid TAN value.' };
      if (!(ph > 0 && ph < 14)) return { error: 'pH must be between 0 and 14.' };
      if (!(t > -2)) return { error: 'Enter a valid temperature.' };
      const tK = t + 273.15;
      const pKa = 0.09018 + 2729.92 / tK;
      const frac = 1 / (1 + Math.pow(10, pKa - ph));
      const nh3 = tan * frac;
      let band = 'Low risk';
      if (nh3 > 0.05) band = 'ACUTE risk, act now';
      else if (nh3 > 0.025) band = 'Chronic risk, reduce';
      return {
        outputs: [
          { label: 'NH₃ fraction of TAN', value: fmt(frac * 100, 2), unit: '%' },
          { label: 'Un-ionised NH₃-N', value: fmt(nh3, 4), unit: 'mg/L', flag: nh3 > 0.025 ? 'over' : 'key' },
          { label: 'Ionised NH₄⁺-N', value: fmt(tan - nh3, 3), unit: 'mg/L' },
          { label: 'Risk', value: band, unit: '', flag: nh3 > 0.025 ? 'over' : null },
          { label: 'Guidance', value: 'Chronic concern ≈ 0.025, acute ≈ 0.05 mg/L NH₃-N for salmonids. Tolerant species differ; check species data.', unit: '' },
        ],
      };
    },
  },

  {
    id: 'oxygenSolubility',
    group: 'Water Quality',
    label: 'Oxygen Solubility',
    blurb: 'DO solubility at temperature, salinity and altitude, with % saturation.',
    method:
      'ln C = A1 + A2(100/T) + A3 ln(T/100) + A4(T/100) + S[B1 + B2(T/100) + B3(T/100)²], C in mL/L, × 1.42905 → mg/L. Barometric correction Cp = Cs × (Pbar − Pwv) / (760 − Pwv), Pbar from altitude, Pwv from temperature.',
    fields: [
      { key: 'temp', label: 'Temperature', unit: '°C', def: '25', hint: 'Culture temperature' },
      { key: 'salinity', label: 'Salinity', unit: 'ppt', def: '0', hint: '0 freshwater, ~35 seawater' },
      { key: 'altitude', label: 'Altitude', unit: 'm', def: '0', hint: 'Site elevation above sea level' },
      { key: 'measured', label: 'Measured DO (optional)', unit: 'mg/L', def: '', hint: 'Enter a probe reading for % saturation' },
    ],
    compute: (v) => {
      const t = n(v.temp);
      const s = n(v.salinity);
      const alt = n(v.altitude);
      const meas = v.measured === '' ? NaN : n(v.measured);
      if (!(t > -2 && t < 60)) return { error: 'Temperature out of range.' };
      const sal = s >= 0 ? s : 0;
      const tK = t + 273.15;
      const Tr = tK / 100;
      const lnC =
        -173.4292 +
        249.6339 * (100 / tK) +
        143.3483 * Math.log(Tr) +
        -21.8492 * Tr +
        sal * (-0.033096 + 0.014259 * Tr + -0.0017 * Tr * Tr);
      const cMlL = Math.exp(lnC);
      let cMgL = cMlL * 1.42905;
      const Pbar = 760 * Math.pow(1 - 2.25577e-5 * (alt > 0 ? alt : 0), 5.25588);
      const Pwv = Math.pow(10, 8.07131 - 1730.63 / (233.426 + t));
      const cAlt = cMgL * ((Pbar - Pwv) / (760 - Pwv));
      const out = [
        { label: 'DO solubility (sea level)', value: fmt(cMgL, 2), unit: 'mg/L' },
        { label: 'DO solubility (at altitude)', value: fmt(cAlt, 2), unit: 'mg/L', flag: 'key' },
      ];
      if (Number.isFinite(meas) && meas >= 0) {
        const pct = (meas / cAlt) * 100;
        out.push({ label: 'Measured saturation', value: fmt(pct, 1), unit: '%', flag: pct < 70 ? 'over' : null });
        out.push({ label: 'O₂ deficit', value: fmt(cAlt - meas, 2), unit: 'mg/L' });
      }
      return { outputs: out };
    },
  },

  {
    id: 'co2Accumulation',
    group: 'Water Quality',
    label: 'CO₂ Accumulation',
    blurb: 'Dissolved CO₂ rise from oxygen consumed across a tank.',
    method:
      'CO₂ produced = 1.375 × O₂ consumed (respiratory mass ratio at RQ ≈ 1.0). Effluent CO₂ = influent + produced. Production rate = ΔCO₂ × flow.',
    fields: [
      { key: 'o2drop', label: 'O₂ consumed across tank', unit: 'mg/L', def: '5', hint: 'Typical single-pass drop 3–6 mg/L' },
      { key: 'co2in', label: 'Influent CO₂', unit: 'mg/L', def: '5', hint: 'Post-degasser CO₂ entering the tank' },
      { key: 'flow', label: 'Tank flow (optional)', unit: 'm³/h', def: '', hint: 'For mass production rate' },
    ],
    compute: (v) => {
      const o2 = n(v.o2drop);
      const cin = n(v.co2in);
      const flow = v.flow === '' ? NaN : n(v.flow);
      if (!(o2 >= 0)) return { error: 'Enter a valid O₂ consumption.' };
      if (!(cin >= 0)) return { error: 'Enter a valid influent CO₂.' };
      const co2made = 1.375 * o2;
      const cout = cin + co2made;
      const out = [
        { label: 'CO₂ produced', value: fmt(co2made, 2), unit: 'mg/L' },
        { label: 'Effluent dissolved CO₂', value: fmt(cout, 2), unit: 'mg/L', flag: cout > 15 ? 'over' : 'key' },
        {
          label: 'Status',
          value: cout > 20 ? 'High, degassing required' : cout > 15 ? 'Above typical RAS limit (~15 mg/L)' : 'Within typical range',
          unit: '',
          flag: cout > 15 ? 'over' : null,
        },
      ];
      if (Number.isFinite(flow) && flow > 0) {
        const kgDay = (flow * co2made * 24) / 1000;
        out.push({ label: 'CO₂ production rate', value: fmt(kgDay, 2), unit: 'kg/day' });
      }
      return { outputs: out };
    },
  },

  {
    id: 'langelier',
    group: 'Water Quality',
    label: 'Langelier Index',
    blurb: 'Scaling vs corrosive tendency, protects plumbing and heat exchangers.',
    method:
      'LSI = pH − pHs. pHs = (9.3 + A + B) − (C + D), A = (log₁₀TDS − 1)/10, B = −13.12·log₁₀T(K) + 34.55, C = log₁₀[Ca as CaCO₃] − 0.4, D = log₁₀[alkalinity as CaCO₃]. LSI > 0 scaling, < 0 corrosive.',
    fields: [
      { key: 'ph', label: 'pH', unit: '', def: '7.6', hint: 'Measured system pH' },
      { key: 'temp', label: 'Temperature', unit: '°C', def: '20', hint: 'Water temperature' },
      { key: 'tds', label: 'Total dissolved solids', unit: 'mg/L', def: '500', hint: 'Estimate from conductivity if unknown' },
      { key: 'calcium', label: 'Calcium hardness (as CaCO₃)', unit: 'mg/L', def: '120', hint: 'From titration' },
      { key: 'alk', label: 'Total alkalinity (as CaCO₃)', unit: 'mg/L', def: '150', hint: 'RAS typical 50–200 mg/L' },
    ],
    compute: (v) => {
      const ph = n(v.ph);
      const t = n(v.temp);
      const tds = n(v.tds);
      const ca = n(v.calcium);
      const alk = n(v.alk);
      if (!(ph > 0 && t > -2 && tds > 0 && ca > 0 && alk > 0))
        return { error: 'All values must be positive.' };
      const tK = t + 273.15;
      const A = (Math.log10(tds) - 1) / 10;
      const B = -13.12 * Math.log10(tK) + 34.55;
      const C = Math.log10(ca) - 0.4;
      const D = Math.log10(alk);
      const pHs = 9.3 + A + B - (C + D);
      const lsi = ph - pHs;
      let interp = 'Balanced, near equilibrium';
      if (lsi > 0.5) interp = 'Scaling, calcium carbonate will deposit';
      else if (lsi > 0.1) interp = 'Slightly scaling';
      else if (lsi < -0.5) interp = 'Corrosive, aggressive water';
      else if (lsi < -0.1) interp = 'Slightly corrosive';
      return {
        outputs: [
          { label: 'pH of saturation (pHs)', value: fmt(pHs, 2), unit: '' },
          { label: 'Langelier index (LSI)', value: fmt(lsi, 2), unit: '', flag: 'key' },
          { label: 'Interpretation', value: interp, unit: '', flag: Math.abs(lsi) > 0.5 ? 'over' : null },
        ],
      };
    },
  },

  {
    id: 'saltMix',
    group: 'Water Quality',
    label: 'Salt Mix',
    blurb: 'Salt mass to reach a target salinity, or dilution water to drop it.',
    method:
      'Salt (kg) = ΔS(g/L) × volume(L) ÷ 1000, taking 1 ppt ≈ 1 g/L. To reduce salinity, freshwater to add = V × (S_current/S_target − 1). Assumes a well-mixed system.',
    fields: [
      { key: 'target', label: 'Target salinity', unit: 'ppt', def: '30', hint: 'Species-specific set point' },
      { key: 'current', label: 'Current salinity', unit: 'ppt', def: '0', hint: 'Measured by refractometer' },
      { key: 'volume', label: 'System volume', unit: 'm³', def: '50', hint: 'Total water volume' },
    ],
    compute: (v) => {
      const tgt = n(v.target);
      const cur = n(v.current);
      const vol = n(v.volume);
      if (!(vol > 0)) return { error: 'System volume must be positive.' };
      if (!(tgt >= 0 && cur >= 0)) return { error: 'Salinity values must be zero or positive.' };
      const diff = tgt - cur;
      const litres = vol * 1000;
      if (diff === 0)
        return { outputs: [{ label: 'Result', value: 'Already at target salinity', unit: '' }] };
      if (diff < 0) {
        if (tgt <= 0)
          return { outputs: [{ label: 'Advice', value: 'Target is zero, full freshwater changeover required', unit: '' }] };
        const addFresh = vol * (cur / tgt - 1);
        return {
          outputs: [
            { label: 'Action', value: 'Dilute with freshwater', unit: '' },
            { label: 'Freshwater to add', value: fmt(addFresh, 2), unit: 'm³', flag: 'key' },
            { label: 'Note', value: 'Assumes negligible salt in the dilution water.', unit: '' },
          ],
        };
      }
      const saltKg = (diff * litres) / 1000;
      return {
        outputs: [
          { label: 'Salinity increase needed', value: fmt(diff, 1), unit: 'ppt' },
          { label: 'Salt to add (approx)', value: fmt(saltKg, 1), unit: 'kg', flag: 'key' },
          { label: 'Note', value: 'Synthetic sea salt typically needs 35 to 40 g/L per 35 ppt. Follow the product spec.', unit: '' },
        ],
      };
    },
  },

  /* ================== BIOFILTER & TREATMENT ================== */
  {
    id: 'biofilter',
    group: 'Biofilter & Treatment',
    label: 'Biofilter Sizing',
    blurb: 'Feed-driven TAN load to media surface area, media and reactor volume.',
    method:
      'TAN production uses eq. 3.9b (feed × protein% × 0.092) when feed protein is given, and the 3% of feed rule of thumb when it is blank. ' +
      'The two agree only near 32.6% protein; above that the 3% rule under-predicts TAN, by ~7% at 35% protein, ~27% at 45% and ~35% at 50%. ' +
      'Media surface area = TAN(g/d) ÷ areal TAN removal rate (ATR). ' +
      'Media volume = area ÷ media SSA. Reactor volume = media ÷ 0.6 fill ratio. O₂ for nitrification = 4.57 × TAN. Alkalinity consumed = 7.14 × TAN. HRT = reactor ÷ flow. ' +
      'This follows Ch.8 Steps 3–5 of the RAS Yellowbook. ATR values: 0.15–0.25 g/m²/day at 12–15 °C, 0.45 in the worked example, up to ~1.2 measured at 26 °C; saltwater derates ATR by ~30%.',
    fields: [
      { key: 'feedLoad', label: 'Feed load', unit: 'kg/day', def: '100', hint: 'From the Feed Rate tool' },
      FEED_PROTEIN_FIELD,
      { key: 'tflowRate', label: 'System flow rate', unit: 'm³/h', def: '60', hint: 'Recirculation flow through the reactor' },
      { key: 'mediaSSA', label: 'Media specific surface area', unit: 'm²/m³', def: '500', hint: 'Kaldnes K1 ≈ 500, K5 ≈ 800' },
      {
        key: 'removalBand',
        label: 'Temperature band',
        unit: '',
        def: 'moderate',
        type: 'select',
        options: [
          { value: 'cold', text: 'Cold water, 12–15 °C (0.20 g/m²/day)' },
          { value: 'moderate', text: 'Moderate (0.45 g/m²/day)' },
          { value: 'warm', text: 'Warm water (0.70 g/m²/day)' },
        ],
        hint:
          'Areal TAN removal rate (ATR) rises with temperature and with reactor TAN concentration. ' +
          'Timmons & Ebeling Ch.8: 0.15–0.25 g/m²/day at 12–15 °C with influent TAN of 1–2 mg/L; ' +
          '0.45 g/m²/day in the worked trickling-tower example; ~1.2 g/m²/day measured on an RBC at 26 °C and TAN 3 mg/L.',
      },
      {
        key: 'salinity',
        label: 'Water type',
        unit: '',
        def: 'fresh',
        type: 'select',
        options: [
          { value: 'fresh', text: 'Freshwater' },
          { value: 'salt', text: 'Saltwater (ATR −30%)' },
        ],
        hint:
          'Timmons & Ebeling Ch.8: "assuming a 30% reduction for saltwater applications is a guideline". ' +
          'Applied to the areal rate above.',
      },
    ],
    compute: (v) => {
      const feed = n(v.feedLoad);
      const flow = n(v.tflowRate);
      let ssa = n(v.mediaSSA);
      if (!(feed > 0 && flow > 0)) return { error: 'Feed load and flow must be positive.' };
      if (!(ssa > 0)) ssa = 500;
      const baseRemoval =
        v.removalBand === 'cold' ? ATR_COLD : v.removalBand === 'warm' ? ATR_WARM : ATR_MODERATE;
      const isSalt = v.salinity === 'salt';
      const removal = isSalt ? baseRemoval * (1 - ATR_SALTWATER_DERATE) : baseRemoval;
      const tan = tanFromFeed(feed, v.feedProtein);
      const tanKg = tan.tanKg;
      const tanG = tanKg * 1000;
      const o2 = tanKg * O2_PER_G_TAN;
      const alk = tanKg * ALK_PER_G_TAN;
      const surfaceArea = tanG / removal;
      const mediaVol = surfaceArea / ssa;
      const reactorVol = mediaVol / 0.6;
      const retention = (reactorVol / flow) * 60;
      const out = [
        { label: 'TAN production', value: fmt(tanKg, 3), unit: 'kg/day' },
        { label: 'TAN method', value: tan.method, unit: '' },
        { label: 'Why this method', value: tan.why, unit: '', flag: tan.basis === 'ruleOfThumb' ? 'over' : null },
        { label: 'O₂ for nitrification', value: fmt(o2, 2), unit: 'kg/day' },
        { label: 'Alkalinity consumed', value: fmt(alk, 2), unit: 'kg CaCO₃/day' },
        {
          label: isSalt ? 'Areal TAN removal rate (saltwater derated)' : 'Areal TAN removal rate',
          value: fmt(removal, 2),
          unit: 'g/m²/day',
        },
        { label: 'Media surface area', value: fmt(surfaceArea, 0), unit: 'm²' },
        { label: 'Media volume', value: fmt(mediaVol, 2), unit: 'm³' },
        { label: 'Reactor volume (60% fill)', value: fmt(reactorVol, 2), unit: 'm³', flag: 'key' },
        { label: 'Reactor retention', value: fmt(retention, 1), unit: 'min' },
      ];
      if (isSalt) {
        out.push({
          label: 'Saltwater derate applied',
          value: `${fmt(baseRemoval, 2)} → ${fmt(removal, 2)} g/m²/day (−30%), per Timmons & Ebeling guideline.`,
          unit: '',
        });
      }
      out.push({
        label: 'Check the rate',
        value: 'ATR depends on reactor TAN concentration as well as temperature. At low influent TAN (1–2 mg/L) the cold-water rate can fall to 0.15. If in doubt, size on the lower rate.',
        unit: '',
      });
      return { outputs: out };
    },
  },

  {
    id: 'dosing',
    group: 'Biofilter & Treatment',
    label: 'Water Quality Dosing',
    blurb: 'Sodium bicarbonate or calcium chloride dosing, on a CaCO₃ basis.',
    method:
      'Alkalinity and calcium are both handled as mg/L as CaCO₃ — the basis a titration reports and the basis the Langelier tool expects. ' +
      'Alkalinity: NaHCO₃ = 1.68 g per mg/L as CaCO₃ per m³. ' +
      'Calcium: CaCl₂·2H₂O = 1.47 g per mg/L as CaCO₃ per m³ (equivalent weight 73.51 against CaCO₃ 50.04). ' +
      'If your calcium figure is reported as elemental Ca, multiply it by 2.5 before entering it. ' +
      'Total dose = dose per m³ × system volume. ' +
      'pH is deliberately not dosed here: the relationship between a pH change and a bicarbonate mass depends on alkalinity, CO₂ and the carbonate equilibrium, so no single factor is defensible.',
    fields: [
      {
        key: 'parameter',
        label: 'Parameter',
        unit: '',
        def: 'alkalinity',
        type: 'select',
        options: [
          { value: 'alkalinity', text: 'Alkalinity (NaHCO₃)' },
          { value: 'calcium', text: 'Calcium (CaCl₂·2H₂O)' },
          { value: 'ph', text: 'pH — guidance only, no dose' },
        ],
      },
      {
        key: 'currentLevel',
        hidden: (v) => v.parameter === 'ph',
        label: (v) =>
          v.parameter === 'calcium'
            ? 'Current calcium hardness (as CaCO₃)'
            : 'Current alkalinity (as CaCO₃)',
        unit: 'mg/L as CaCO₃',
        def: '60',
        hint: (v) =>
          v.parameter === 'calcium'
            ? 'From titration, reported as CaCO₃ — the same basis the Langelier tool uses. If your lab reports elemental Ca, multiply by 2.5.'
            : 'From titration, as CaCO₃',
      },
      {
        key: 'targetLevel',
        hidden: (v) => v.parameter === 'ph',
        label: (v) =>
          v.parameter === 'calcium'
            ? 'Target calcium hardness (as CaCO₃)'
            : 'Target alkalinity (as CaCO₃)',
        unit: 'mg/L as CaCO₃',
        def: '120',
        hint: (v) =>
          v.parameter === 'calcium'
            ? 'Typical RAS 80–200 mg/L as CaCO₃'
            : 'RAS target 50–200 mg/L as CaCO₃',
      },
      {
        key: 'tankVolume',
        hidden: (v) => v.parameter === 'ph',
        label: 'System volume',
        unit: 'L',
        def: '50000',
        hint: 'Total recirculating volume',
      },
    ],
    compute: (v) => {
      // pH is not dosed directly. A gram figure per pH unit would require a
      // fixed pH-to-alkalinity relationship, which does not exist — it moves
      // with alkalinity, CO₂ and the carbonate equilibrium.
      if (v.parameter === 'ph') {
        return {
          outputs: [
            { label: 'No dose calculated', value: 'pH is not dosed directly', unit: '' },
            {
              label: 'Why',
              value: 'There is no fixed mass of bicarbonate per unit of pH. The response depends on your current alkalinity, dissolved CO₂ and the carbonate equilibrium, so any single conversion factor would be invented.',
              unit: '',
            },
            {
              label: 'What to do instead',
              value: 'In RAS, pH is managed through alkalinity. Switch this tool to Alkalinity (NaHCO₃), dose toward 50–200 mg/L as CaCO₃, and let pH follow.',
              unit: '',
              flag: 'key',
            },
            {
              label: 'Also check',
              value: 'A low pH that resists alkalinity correction usually means CO₂ accumulation. Use the CO₂ Degasser Air tool — stripping CO₂ raises pH without adding chemicals.',
              unit: '',
            },
            {
              label: 'Dosing practice',
              value: 'Move alkalinity gradually, re-test between additions, and never bulk-dose to chase a pH reading.',
              unit: '',
            },
          ],
        };
      }

      const cur = n(v.currentLevel);
      const tgt = n(v.targetLevel);
      const volL = n(v.tankVolume);
      if (!Number.isFinite(volL) || !(volL > 0))
        return { error: 'Enter a system volume greater than zero.' };
      if (!Number.isFinite(cur))
        return { error: 'Enter the current level in mg/L as CaCO₃.' };
      if (!Number.isFinite(tgt))
        return { error: 'Enter the target level in mg/L as CaCO₃.' };
      if (cur < 0 || tgt < 0) return { error: 'Levels cannot be negative.' };
      const diff = tgt - cur;
      if (diff === 0)
        return { outputs: [{ label: 'Result', value: 'Already at target', unit: '' }] };
      if (diff < 0)
        return {
          outputs: [
            { label: 'Result', value: 'Target below current', unit: '' },
            { label: 'Advice', value: 'Dilute or water-change instead of dosing', unit: '' },
          ],
        };
      const volM3 = volL / 1000;
      if (v.parameter === 'alkalinity') {
        const perM3 = NAHCO3_G_PER_MGL_CACO3_PER_M3 * diff;
        const g = perM3 * volM3;
        return {
          outputs: [
            { label: 'Chemical', value: 'Sodium bicarbonate (NaHCO₃)', unit: '' },
            { label: 'Basis', value: 'Raising alkalinity, mg/L as CaCO₃', unit: '' },
            { label: 'Increase required', value: fmt(diff, 1), unit: 'mg/L as CaCO₃' },
            { label: 'Dose per m³', value: fmt(perM3, 1), unit: 'g' },
            { label: 'Total dose', value: fmt(g, 0), unit: 'g', flag: 'key' },
            { label: 'Total dose', value: fmt(g / 1000, 2), unit: 'kg' },
            { label: 'Advice', value: 'Add over several hours, re-test before further dosing', unit: '' },
          ],
        };
      }
      if (v.parameter === 'calcium') {
        const perM3 = CACL2_G_PER_MGL_CACO3_PER_M3 * diff;
        const g = perM3 * volM3;
        return {
          outputs: [
            { label: 'Chemical', value: 'Calcium chloride dihydrate (CaCl₂·2H₂O)', unit: '' },
            { label: 'Basis', value: 'Raising calcium hardness, mg/L as CaCO₃', unit: '' },
            { label: 'Increase required', value: fmt(diff, 1), unit: 'mg/L as CaCO₃' },
            { label: 'Dose per m³', value: fmt(perM3, 1), unit: 'g' },
            { label: 'Total dose', value: fmt(g, 0), unit: 'g', flag: 'key' },
            { label: 'Total dose', value: fmt(g / 1000, 2), unit: 'kg' },
            {
              label: 'Check the basis',
              value: 'This assumes your figure is as CaCO₃. If it is elemental Ca, multiply your input by 2.5 first — otherwise this dose is 2.5× too low.',
              unit: '',
            },
            { label: 'Advice', value: 'Dissolve fully before adding, then re-test', unit: '' },
          ],
        };
      }
      return { error: 'Select a parameter to dose.' };
    },
  },

  {
    id: 'co2Degasser',
    group: 'Biofilter & Treatment',
    label: 'CO₂ Degasser Air',
    blurb: 'Stripping air requirement for a packed degasser, first-pass estimate.',
    method:
      'Removal % = (Cin − Cout) ÷ Cin. Stripping air flow = air:water ratio (G:L) × water flow. CO₂ removed = (Cin − Cout) × flow. This is a sizing estimate, not a mass-transfer design.',
    fields: [
      { key: 'flow', label: 'Water flow', unit: 'm³/h', def: '60', hint: 'Flow through the degasser' },
      { key: 'co2in', label: 'Influent CO₂', unit: 'mg/L', def: '20', hint: 'CO₂ entering the column' },
      { key: 'co2out', label: 'Target effluent CO₂', unit: 'mg/L', def: '8', hint: 'RAS target generally < 10–15 mg/L' },
      { key: 'gl', label: 'Air to water ratio (G:L)', unit: '', def: '7', hint: 'Typical 5:1 to 10:1 for CO₂ stripping' },
    ],
    compute: (v) => {
      const flow = n(v.flow);
      const cin = n(v.co2in);
      const cout = n(v.co2out);
      const gl = n(v.gl);
      if (!(flow > 0)) return { error: 'Water flow must be positive.' };
      if (!(cin > 0 && cout >= 0 && cout < cin))
        return { error: 'Target CO₂ must be below influent CO₂.' };
      if (!(gl > 0)) return { error: 'Air to water ratio must be positive.' };
      const removalPct = ((cin - cout) / cin) * 100;
      const airFlow = gl * flow;
      const kgDay = ((cin - cout) * flow * 24) / 1000;
      return {
        outputs: [
          { label: 'Required removal', value: fmt(removalPct, 1), unit: '%', flag: removalPct > 70 ? 'over' : null },
          { label: 'Stripping air flow', value: fmt(airFlow, 0), unit: 'm³/h', flag: 'key' },
          { label: 'CO₂ removed', value: fmt(kgDay, 2), unit: 'kg/day' },
          { label: 'Note', value: 'Above ~70% removal needs a taller column or higher G:L. Confirm with packing-specific design.', unit: '' },
        ],
      };
    },
  },

  /* ====================== PRODUCTION ====================== */
  {
    id: 'stockingDensity',
    group: 'Production',
    label: 'Stocking Density',
    blurb: 'Maximum biomass and fish count from tank volume and target density.',
    method:
      'Max biomass = tank volume × target density. Max fish count = max biomass × 1000 ÷ average weight. Resulting density = (count × weight) ÷ (volume × 1000).',
    fields: [
      { key: 'tankVolume', label: 'Tank volume', unit: 'm³', def: '50', hint: 'Working volume' },
      { key: 'maxDensity', label: 'Target max density', unit: 'kg/m³', def: '40', hint: 'Species/water-quality dependent, often 40–80' },
      { key: 'avgFishWeight', label: 'Average fish weight', unit: 'g', def: '250', hint: 'Mean individual weight' },
    ],
    compute: (v) => {
      const vol = n(v.tankVolume);
      const dens = n(v.maxDensity);
      const w = n(v.avgFishWeight);
      if (!(vol > 0 && dens > 0 && w > 0))
        return { error: 'All values must be positive.' };
      const maxBiomass = vol * dens;
      const numFish = Math.floor((maxBiomass * 1000) / w);
      if (numFish <= 0) return { error: 'Fish weight too heavy for this tank.' };
      const actual = (w * numFish) / (vol * 1000);
      return {
        outputs: [
          { label: 'Max biomass', value: fmt(maxBiomass, 0), unit: 'kg' },
          { label: 'Max fish count', value: numFish.toLocaleString('en-GB'), unit: '', flag: 'key' },
          { label: 'Resulting density', value: fmt(actual, 2), unit: 'kg/m³' },
          {
            label: 'Note',
            value: dens > 80 ? 'High density, strong O₂ and biofilter required' : 'Moderate density',
            unit: '',
          },
        ],
      };
    },
  },

  {
    id: 'feedRate',
    group: 'Production',
    label: 'Feed Rate',
    blurb: 'Daily ration from biomass and feed rate. Feed this into Biofilter Sizing.',
    method:
      'Biomass = fish count × average weight ÷ 1000. Daily feed = biomass × feed rate %. ' +
      'Implied TAN load uses eq. 3.9b (feed × protein% × 0.092) when feed protein is given, otherwise the 3% of feed rule of thumb, which assumes roughly 32.6% protein.',
    fields: [
      { key: 'fishCount', label: 'Number of fish', unit: 'fish', def: '5000', hint: 'Current population' },
      { key: 'avgWeight', label: 'Average weight', unit: 'g', def: '250', hint: 'Mean individual weight' },
      { key: 'feedPct', label: 'Feed rate', unit: '% body wt/day', def: '1.5', hint: 'Fingerlings 3–5%, grow-out 1–2%' },
      { key: 'meals', label: 'Meals per day', unit: '', def: '3', hint: 'Feeding frequency' },
      FEED_PROTEIN_FIELD,
    ],
    compute: (v) => {
      const count = n(v.fishCount);
      const w = n(v.avgWeight);
      const pct = n(v.feedPct);
      const meals = n(v.meals);
      if (!(count > 0 && w > 0 && pct > 0))
        return { error: 'Fish count, weight and feed rate must be positive.' };
      const biomass = (count * w) / 1000;
      const daily = biomass * (pct / 100);
      const out = [
        { label: 'Total biomass', value: fmt(biomass, 1), unit: 'kg' },
        { label: 'Daily feed', value: fmt(daily, 2), unit: 'kg/day', flag: 'key' },
      ];
      if (meals > 0) out.push({ label: 'Feed per meal', value: fmt(daily / meals, 3), unit: 'kg' });
      const tan = tanFromFeed(daily, v.feedProtein);
      out.push({ label: 'Implied TAN load', value: fmt(tan.tanKg, 3), unit: 'kg/day' });
      out.push({ label: 'TAN method', value: tan.method, unit: '' });
      out.push({
        label: 'Why this method',
        value: tan.why,
        unit: '',
        flag: tan.basis === 'ruleOfThumb' ? 'over' : null,
      });
      return { outputs: out };
    },
  },

  {
    id: 'fcr',
    group: 'Production',
    label: 'Feed Conversion (FCR)',
    blurb: 'Feed conversion ratio and feed cost per kg of fish produced.',
    method:
      'FCR = total feed fed ÷ biomass gain. Feed per kg gain = FCR. Feed cost per kg fish = FCR × feed price.',
    fields: [
      { key: 'feedFed', label: 'Total feed fed', unit: 'kg', def: '1500', hint: 'Cumulative over the period' },
      { key: 'gain', label: 'Biomass gain', unit: 'kg', def: '1000', hint: 'Harvest biomass minus stocked' },
      { key: 'feedPrice', label: 'Feed price (optional)', unit: '$/kg', def: '', hint: 'For cost per kg produced' },
    ],
    compute: (v) => {
      const feed = n(v.feedFed);
      const gain = n(v.gain);
      const price = v.feedPrice === '' ? NaN : n(v.feedPrice);
      if (!(feed > 0 && gain > 0)) return { error: 'Feed and gain must be positive.' };
      const fcr = feed / gain;
      const out = [
        { label: 'FCR', value: fmt(fcr, 2), unit: ': 1', flag: 'key' },
        { label: 'Feed per kg gain', value: fmt(fcr, 2), unit: 'kg' },
        {
          label: 'Performance',
          value: fcr < 1.0 ? 'Excellent' : fcr < 1.3 ? 'Good' : fcr < 1.8 ? 'Acceptable' : 'Poor, review feeding',
          unit: '',
          flag: fcr >= 1.8 ? 'over' : null,
        },
      ];
      if (Number.isFinite(price) && price > 0) {
        out.push({ label: 'Feed cost per kg fish', value: '$' + fmt(fcr * price, 2), unit: '' });
        out.push({ label: 'Total feed cost', value: '$' + fmt(feed * price, 0), unit: '' });
      }
      return { outputs: out };
    },
  },

  {
    id: 'growthProjection',
    group: 'Production',
    label: 'Growth Projection',
    blurb: 'Project weight forward using a thermal growth coefficient or SGR.',
    method:
      'TGC (×1000 convention): Wf = (Wi^(1/3) + TGC × T × days ÷ 1000)³, TGC typically 1.5–2.5 on this scale. SGR: Wf = Wi × e^((SGR/100) × days). Days to target solved from the same relation. Note: a second TGC convention uses Wf^(1/3) − Wi^(1/3) = TGC·Σ(T·days) with TGC ≈ 0.0015–0.0025; this tool uses the ×1000 form, so enter 2.0 not 0.002.',
    fields: [
      {
        key: 'model',
        label: 'Growth model',
        unit: '',
        def: 'tgc',
        type: 'select',
        options: [
          { value: 'tgc', text: 'Thermal growth coefficient (TGC)' },
          { value: 'sgr', text: 'Specific growth rate (SGR)' },
        ],
      },
      { key: 'wi', label: 'Initial weight', unit: 'g', def: '50', hint: 'Stocking weight' },
      { key: 'temp', label: 'Average temperature', unit: '°C', def: '15', hint: 'Mean culture temperature (TGC only)' },
      { key: 'days', label: 'Grow-out period', unit: 'days', def: '180', hint: 'Projection horizon' },
      { key: 'coeff', label: 'TGC or SGR', unit: '', def: '2.0', hint: 'TGC on ×1000 scale ≈ 1.5–2.5 (enter 2.0, not 0.002); SGR in %/day' },
      { key: 'target', label: 'Target weight (optional)', unit: 'g', def: '', hint: 'For days-to-target' },
    ],
    compute: (v) => {
      const wi = n(v.wi);
      const t = n(v.temp);
      const days = n(v.days);
      const k = n(v.coeff);
      const tgt = v.target === '' ? NaN : n(v.target);
      if (!(wi > 0 && days > 0 && k > 0)) return { error: 'Weight, days and coefficient must be positive.' };
      let wf, daysToTarget;
      if (v.model === 'sgr') {
        wf = wi * Math.exp((k / 100) * days);
        if (Number.isFinite(tgt) && tgt > wi)
          daysToTarget = Math.log(tgt / wi) / (k / 100);
      } else {
        if (!(t > 0)) return { error: 'Temperature must be positive for the TGC model.' };
        wf = Math.pow(Math.cbrt(wi) + (k / 1000) * t * days, 3);
        if (Number.isFinite(tgt) && tgt > wi)
          daysToTarget = (Math.cbrt(tgt) - Math.cbrt(wi)) / ((k / 1000) * t);
      }
      const out = [
        { label: 'Projected final weight', value: fmt(wf, 1), unit: 'g', flag: 'key' },
        { label: 'Total gain', value: fmt(wf - wi, 1), unit: 'g' },
      ];
      if (Number.isFinite(daysToTarget))
        out.push({ label: 'Days to target weight', value: fmt(daysToTarget, 0), unit: 'days' });
      return { outputs: out };
    },
  },

  {
    id: 'carryingCapacity',
    group: 'Production',
    label: 'Carrying Capacity',
    blurb: 'Oxygen-limited ceiling on feed and biomass for a given flow.',
    method:
      'Available O₂ (kg/d) = flow(m³/h) × 1000 × (DOin − DOmin) × 24 ÷ 1e6. Max feed = available O₂ ÷ design O₂ per kg feed. Max biomass = max feed ÷ feed rate %. ' +
      'Uses the same 1.0 kg O₂/kg feed design basis as System O₂ Demand (Timmons & Ebeling), so the two tools agree.',
    fields: [
      { key: 'flow', label: 'System flow', unit: 'm³/h', def: '60', hint: 'Total flow delivering O₂' },
      { key: 'doIn', label: 'Influent DO', unit: 'mg/L', def: '12', hint: 'Post-oxygenation, can exceed saturation' },
      { key: 'doMin', label: 'Minimum allowable DO', unit: 'mg/L', def: '6', hint: 'Species lower limit, often 5–6' },
      {
        key: 'o2PerFeed',
        label: 'Design O₂ per kg feed',
        unit: 'kg O₂/kg',
        def: String(O2_PER_FEED_DESIGN),
        hint: 'Same basis as System O₂ Demand. Yellowbook design value 1.0; theoretical floor ≈0.37. A lower figure raises the feed ceiling — justify it before using one.',
      },
      { key: 'feedPct', label: 'Feed rate', unit: '% body wt/day', def: '1.5', hint: 'Grow-out 1–2%' },
    ],
    compute: (v) => {
      const flow = n(v.flow);
      const di = n(v.doIn);
      const dm = n(v.doMin);
      const opf = n(v.o2PerFeed);
      const pct = n(v.feedPct);
      if (!(flow > 0 && di > dm && dm >= 0))
        return { error: 'Check flow and DO values (influent must exceed minimum).' };
      if (!(opf > 0 && pct > 0)) return { error: 'O₂ per feed and feed rate must be positive.' };
      const o2AvailKgDay = (flow * 1000 * (di - dm) * 24) / 1e6;
      const maxFeed = o2AvailKgDay / opf;
      const maxBiomass = maxFeed / (pct / 100);
      return {
        outputs: [
          { label: 'Available O₂', value: fmt(o2AvailKgDay, 2), unit: 'kg/day' },
          { label: 'Max feed load', value: fmt(maxFeed, 2), unit: 'kg/day', flag: 'key' },
          { label: 'Max biomass', value: fmt(maxBiomass, 0), unit: 'kg' },
          { label: 'Note', value: 'O₂ ceiling only. Biofilter and CO₂ stripping must also keep up at this feed load.', unit: '' },
        ],
      };
    },
  },

  {
    id: 'systemOxygenDemand',
    group: 'Production',
    label: 'System O₂ Demand',
    blurb: 'Oxygen demand for equipment sizing, with the theoretical floor shown separately.',
    method:
      'Sizing basis = feed × design O₂ factor. Timmons & Ebeling recommend 1.0 kg O₂/kg feed as "a safe starting point" for design. ' +
      'The stoichiometric floor is shown alongside for reference only: respiration (feed × respiration factor) + nitrification (4.57 × TAN), ' +
      'which lands near 0.37 kg O₂/kg feed. That floor is a theoretical minimum and must not be used to size oxygen supply. ' +
      'Gas volume = O₂ kg ÷ 1.429 kg/m³ (STP).',
    fields: [
      { key: 'feed', label: 'Feed load', unit: 'kg/day', def: '100', hint: 'From the Feed Rate tool' },
      FEED_PROTEIN_FIELD,
      {
        key: 'designFactor',
        label: 'Design O₂ per kg feed (sizing basis)',
        unit: 'kg O₂/kg',
        def: String(O2_PER_FEED_DESIGN),
        hint: 'Yellowbook design rule of thumb 1.0. Use 0.5 only where a trickling filter supplies nitrifier O₂ directly.',
      },
      {
        key: 'respFactor',
        label: 'Respiration O₂ per kg feed (floor reference only)',
        unit: 'kg O₂/kg',
        def: String(O2_RESP_PER_FEED_FLOOR),
        hint: 'Fish metabolism alone, 0.2–0.3. Feeds the lower-bound line only, never the sizing basis.',
      },
    ],
    compute: (v) => {
      const feed = n(v.feed);
      const df = n(v.designFactor);
      const rf = n(v.respFactor);
      if (!(feed > 0)) return { error: 'Feed load must be positive.' };
      if (!(df > 0)) return { error: 'Design O₂ factor must be positive.' };
      if (!(rf > 0)) return { error: 'Respiration factor must be positive.' };

      const design = feed * df;
      const designM3 = design / 1.429;

      const resp = feed * rf;
      const tan = tanFromFeed(feed, v.feedProtein);
      const nitr = tan.tanKg * O2_PER_G_TAN;
      const floor = resp + nitr;
      const floorPerFeed = floor / feed;

      const out = [
        { label: 'Design O₂ demand (size equipment to this)', value: fmt(design, 2), unit: 'kg/day', flag: 'key' },
        { label: 'Design O₂ demand (size equipment to this)', value: fmt(design / 24, 3), unit: 'kg/h' },
        { label: 'Gas volume at design demand (STP)', value: fmt(designM3, 1), unit: 'm³/day' },
        { label: '— Theoretical floor, reference only —', value: '', unit: '' },
        { label: 'Floor: respiration component', value: fmt(resp, 2), unit: 'kg/day' },
        { label: 'Floor: nitrification component', value: fmt(nitr, 2), unit: 'kg/day' },
        { label: 'Floor: TAN method', value: tan.method, unit: '' },
        { label: 'Floor: why this method', value: tan.why, unit: '' },
        {
          label: 'Stoichiometric floor (NOT a sizing basis)',
          value: fmt(floor, 2),
          unit: 'kg/day',
        },
        { label: 'Floor as O₂ per kg feed', value: fmt(floorPerFeed, 3), unit: 'kg O₂/kg' },
      ];
      if (df < floorPerFeed) {
        out.push({
          label: 'Warning',
          value: 'Design factor is below the theoretical floor. Oxygen supply sized on this will be inadequate.',
          unit: '',
          flag: 'over',
        });
      }
      out.push({
        label: 'Note',
        value: 'The floor assumes perfect transfer and no losses. Real systems never reach it. Timmons & Ebeling recommend designing at 1.0 kg O₂/kg feed.',
        unit: '',
      });
      return { outputs: out };
    },
  },

  /* ====================== HYDRAULICS ====================== */
  {
    id: 'pumpHead',
    group: 'Hydraulics',
    label: 'Pump Head',
    blurb: 'Total dynamic head and pump power, Darcy-Weisbach friction.',
    method:
      'v = Q ÷ A. hf = (0.02 L ÷ D) × (v² ÷ 2g), D in metres (pipe diameter is entered in mm and converted). Fittings = 30% of hf. TDH = static + hf + fittings. ' +
      'Hydraulic (water) power = ρgQH. Shaft power = hydraulic ÷ pump efficiency (from the input). ' +
      'Motor rating must equal or exceed shaft power: shaft kW ÷ 0.746 = shaft HP, then round up to the next standard frame. ' +
      'Pump efficiency is applied once, to get shaft power from hydraulic power — it is not applied again when converting to HP.',
    fields: [
      { key: 'flowRate', label: 'Flow rate', unit: 'm³/h', def: '36', hint: 'Duty point flow' },
      { key: 'pipeDiameter', label: 'Pipe internal diameter', unit: 'mm', def: '90', hint: 'Internal bore. A 110 mm pipe is 110, not 0.11.' },
      { key: 'pipeLength', label: 'Pipe length', unit: 'm', def: '40', hint: 'Total developed length' },
      { key: 'elevation', label: 'Static lift', unit: 'm', def: '3', hint: 'Geometric head' },
      {
        key: 'pumpEfficiency',
        label: 'Pump efficiency',
        unit: '',
        def: String(PUMP_EFFICIENCY),
        hint: 'Wire-to-water, as a fraction 0–1. Centrifugal pumps typically 0.6–0.8; small RAS pumps often 0.5–0.65.',
      },
    ],
    compute: (v) => {
      const flow = n(v.flowRate);
      const diaMm = n(v.pipeDiameter);
      const len = n(v.pipeLength);
      const elev = n(v.elevation);
      const eff = n(v.pumpEfficiency);
      if (!(flow > 0 && diaMm > 0 && len > 0 && elev >= 0))
        return { error: 'All positive values required.' };
      if (!(eff > 0 && eff <= 1))
        return { error: 'Pump efficiency must be a fraction between 0 and 1.' };
      const dia = diaMm / 1000; // formula needs metres
      const flowMs3 = flow / 3600;
      const area = Math.PI * Math.pow(dia / 2, 2);
      const velocity = flowMs3 / area;
      const g = 9.81;
      const friction = ((0.02 * len) / dia) * (Math.pow(velocity, 2) / (2 * g));
      const fittings = friction * 0.3;
      const totalHead = elev + friction + fittings;

      // Hydraulic (water) power: the power actually delivered to the water.
      const hydraulicKw = (WATER_DENSITY * g * flowMs3 * totalHead) / 1000;
      // Shaft power: what the pump must absorb. Efficiency is applied HERE,
      // once. The previous version divided by it again when converting to HP,
      // which inflated the motor by 1/0.75 and reported a full frame too large.
      const shaftKw = hydraulicKw / eff;
      const shaftHp = shaftKw / KW_PER_HP;
      const frameHp = MOTOR_FRAMES_HP.find((f) => f >= shaftHp) ?? null;

      const diaWarn = pipeDiameterWarning(diaMm);
      const out = [];
      if (diaWarn) out.push({ label: 'Check the diameter', value: diaWarn, unit: '', flag: 'over' });
      out.push(
          { label: 'Total head', value: fmt(totalHead, 2), unit: 'm', flag: 'key' },
          { label: 'Friction loss', value: fmt(friction, 2), unit: 'm' },
          { label: 'Velocity', value: fmt(velocity, 2), unit: 'm/s', flag: velocity > 1.5 ? 'over' : null },
          { label: 'Hydraulic power (ρgQH)', value: fmt(hydraulicKw, 3), unit: 'kW' },
          { label: `Pump efficiency`, value: fmt(eff * 100, 0), unit: '%' },
          { label: 'Shaft power required', value: fmt(shaftKw, 3), unit: 'kW', flag: 'key' },
          { label: 'Shaft power required', value: fmt(shaftHp, 2), unit: 'HP' },
          {
            label: 'Next standard motor frame',
            value: frameHp != null ? fmt(frameHp, 2) : 'above ' + MOTOR_FRAMES_HP[MOTOR_FRAMES_HP.length - 1],
            unit: 'HP',
          },
          {
            label: 'Status',
            value: velocity > 1.5 ? 'High velocity, consider larger pipe' : 'Velocity OK',
            unit: '',
          },
          {
            label: 'Motor selection',
            value: 'The frame shown is the smallest that covers shaft power. Add margin for duty-point drift, and check the motor’s own efficiency and service factor against the manufacturer’s curve.',
            unit: '',
          },
      );
      return { outputs: out };
    },
  },

  {
    id: 'pipeSizing',
    group: 'Hydraulics',
    label: 'Pipe Sizing',
    blurb: 'Required pipe bore for a target flow and velocity.',
    method:
      'Required ID = √(4Q ÷ (π v)). Velocity at a given ID = Q ÷ (πD²/4). RAS target band 1.0 to 1.5 m/s; below 0.6 m/s solids settle.',
    fields: [
      { key: 'flow', label: 'Flow rate', unit: 'L/min', def: '600', hint: 'Design flow' },
      { key: 'vTarget', label: 'Target velocity', unit: 'm/s', def: '1.2', hint: 'RAS band 1.0–1.5 m/s' },
      { key: 'actualID', label: 'Actual internal diameter (optional)', unit: 'mm', def: '', hint: 'Check velocity at a real pipe size, in mm' },
    ],
    compute: (v) => {
      const flow = n(v.flow);
      const vt = n(v.vTarget);
      const actual = v.actualID === '' ? NaN : n(v.actualID);
      if (!(flow > 0 && vt > 0)) return { error: 'Flow and target velocity must be positive.' };
      const q = flow / 1000 / 60;
      const reqD = Math.sqrt((4 * q) / (Math.PI * vt)) * 1000;
      const out = [
        { label: 'Required internal diameter', value: fmt(reqD, 1), unit: 'mm', flag: 'key' },
        { label: 'Flow', value: fmt(q * 1000, 2), unit: 'L/s' },
      ];
      if (Number.isFinite(actual) && actual > 0) {
        const diaWarn = pipeDiameterWarning(actual);
        if (diaWarn) out.push({ label: 'Check the diameter', value: diaWarn, unit: '', flag: 'over' });
        const aVel = q / (Math.PI / 4 * Math.pow(actual / 1000, 2));
        out.push({
          label: 'Velocity at ' + fmt(actual, 0) + ' mm',
          value: fmt(aVel, 2),
          unit: 'm/s',
          flag: aVel > 1.5 || aVel < 0.6 ? 'over' : null,
        });
        out.push({
          label: 'Assessment',
          value: aVel > 1.5 ? 'Too fast, head loss and noise' : aVel < 0.6 ? 'Too slow, solids may settle' : 'Within RAS band',
          unit: '',
        });
      }
      return { outputs: out };
    },
  },

  {
    id: 'headLossHW',
    group: 'Hydraulics',
    label: 'Head Loss (Hazen-Williams)',
    blurb: 'Friction head for a pipe run. Complements the Darcy Pump Head tool.',
    method:
      'hf = 10.67 × L × Q^1.852 ÷ (C^1.852 × D^4.87), SI units (Q m³/s, D m, L m). Fittings allowance as a percentage of hf.',
    fields: [
      { key: 'flow', label: 'Flow rate', unit: 'L/min', def: '600', hint: 'Design flow' },
      { key: 'id', label: 'Pipe internal diameter', unit: 'mm', def: '90', hint: 'Internal bore, in mm. A 110 mm pipe is 110, not 0.11.' },
      { key: 'len', label: 'Pipe length', unit: 'm', def: '40', hint: 'Developed length' },
      { key: 'c', label: 'Hazen-Williams C', unit: '', def: '150', hint: 'PVC ≈ 150' },
      { key: 'fit', label: 'Fittings allowance', unit: '% of friction', def: '30', hint: '20–40% typical' },
    ],
    compute: (v) => {
      const flow = n(v.flow);
      const id = n(v.id);
      const len = n(v.len);
      const c = n(v.c);
      const fit = n(v.fit);
      if (!(flow > 0 && id > 0 && len > 0 && c > 0))
        return { error: 'Flow, diameter, length and C must be positive.' };
      const q = flow / 1000 / 60;
      const d = id / 1000; // formula needs metres
      const vel = q / (Math.PI / 4 * d * d);
      const hf = (10.67 * len * Math.pow(q, 1.852)) / (Math.pow(c, 1.852) * Math.pow(d, 4.87));
      const fitHead = hf * (fit / 100);
      const diaWarn = pipeDiameterWarning(id);
      const out = [];
      if (diaWarn) out.push({ label: 'Check the diameter', value: diaWarn, unit: '', flag: 'over' });
      out.push(
        { label: 'Friction loss (pipe)', value: fmt(hf, 2), unit: 'm' },
        { label: 'Fittings loss', value: fmt(fitHead, 2), unit: 'm' },
        { label: 'Total friction head', value: fmt(hf + fitHead, 2), unit: 'm', flag: 'key' },
        { label: 'Velocity', value: fmt(vel, 2), unit: 'm/s', flag: vel > 1.5 ? 'over' : null },
      );
      return { outputs: out };
    },
  },

  /*
   * The 'flowRate' calculator was removed. It solved Q = (πD²/4) × v, deriving
   * flow from diameter and velocity — backwards for design work, where flow is
   * the known quantity and diameter or velocity is what you are solving for.
   * Pipe Sizing already covers both real directions: required bore from flow
   * and target velocity, and resulting velocity at a chosen bore.
   */

  {
    id: 'pipeVolume',
    group: 'Hydraulics',
    label: 'Pipe Volume',
    blurb: 'Internal pipe capacity and liquid mass.',
    method: 'V = (πD²/4) × L, with D in metres (diameter is entered in mm and converted). Liquid mass ≈ V(L) × 1.0 for water.',
    fields: [
      { key: 'diameter', label: 'Pipe internal diameter', unit: 'mm', def: '90', hint: 'Internal bore. A 110 mm pipe is 110, not 0.11.' },
      { key: 'length', label: 'Pipe length', unit: 'm', def: '20', hint: 'Run length' },
    ],
    compute: (v) => {
      const dMm = n(v.diameter);
      const l = n(v.length);
      if (!(dMm > 0 && l > 0)) return { error: 'Values must be greater than zero.' };
      const d = dMm / 1000; // formula needs metres
      const vol = Math.PI * Math.pow(d / 2, 2) * l;
      const diaWarn = pipeDiameterWarning(dMm);
      const out = [];
      if (diaWarn) out.push({ label: 'Check the diameter', value: diaWarn, unit: '', flag: 'over' });
      out.push(
        { label: 'Pipe volume', value: fmt(vol * 1000, 2), unit: 'L', flag: 'key' },
        { label: 'Liquid mass', value: fmt(vol * 1000, 2), unit: 'kg' },
      );
      return { outputs: out };
    },
  },

  /* ==================== SOLIDS & TANKS ==================== */
  {
    id: 'tankVolume',
    group: 'Solids & Tanks',
    label: 'Tank Volume',
    blurb: 'Volume for rectangular, cylindrical, or capsule tanks.',
    method:
      'Rectangular: L × W × H. Cylindrical: (πD²/4) × H. Capsule: πr²(4r/3 + L), a cylindrical barrel of length L closed by hemispherical ends of radius r, so overall height is L + 2r. ' +
      'Filled volume uses the filled height where given, otherwise the tank is taken as full. ' +
      'For a capsule the partial fill is solved by region: spherical cap πd²(3r − d)/3 in the bottom end, plus a cylindrical slice, plus a cap of the top end. Litres = m³ × 1000.',
    fields: [
      {
        key: 'shape',
        label: 'Shape',
        unit: '',
        def: 'cylindrical',
        type: 'select',
        options: [
          { value: 'rectangular', text: 'Rectangular' },
          { value: 'cylindrical', text: 'Cylindrical' },
          { value: 'capsule', text: 'Capsule' },
        ],
      },
      { key: 'length', label: 'Length (rect / capsule)', unit: 'm', def: '4', hint: 'Internal length' },
      { key: 'width', label: 'Width (rectangular)', unit: 'm', def: '2', hint: 'Internal width' },
      { key: 'height', label: 'Height', unit: 'm', def: '1.5', hint: 'Water depth' },
      { key: 'diameter', label: 'Diameter (cyl / capsule)', unit: 'm', def: '6', hint: 'Internal diameter' },
      { key: 'filledHeight', label: 'Filled height (optional)', unit: 'm', def: '', hint: 'If part-filled' },
    ],
    compute: (v) => {
      const shape = v.shape;
      const L = n(v.length);
      const W = n(v.width);
      const H = n(v.height);
      const D = n(v.diameter);
      const fh = v.filledHeight === '' ? NaN : n(v.filledHeight);
      let volume, filled;
      if (Number.isFinite(fh) && fh < 0)
        return { error: 'Filled height cannot be negative.' };
      if (shape === 'rectangular') {
        if (!(L > 0 && W > 0 && H > 0)) return { error: 'Enter valid length, width and height.' };
        if (Number.isFinite(fh) && fh > H)
          return { error: `Filled height cannot exceed the tank height of ${fmt(H, 3)} m.` };
        volume = L * W * H;
        filled = L * W * (Number.isFinite(fh) ? fh : H);
      } else if (shape === 'cylindrical') {
        if (!(D > 0 && H > 0)) return { error: 'Enter valid diameter and height.' };
        if (Number.isFinite(fh) && fh > H)
          return { error: `Filled height cannot exceed the tank height of ${fmt(H, 3)} m.` };
        const r = D / 2;
        volume = Math.PI * r * r * H;
        filled = Math.PI * r * r * (Number.isFinite(fh) ? fh : H);
      } else {
        if (!(D > 0 && L > 0)) return { error: 'Enter valid diameter and length.' };
        const r = D / 2;
        volume = Math.PI * r * r * ((4 / 3) * r + L);
        // Overall height of a capsule is the cylindrical run plus both
        // hemispherical ends. Filled height is measured from the bottom.
        const capsuleHeight = L + 2 * r;
        if (Number.isFinite(fh)) {
          if (fh > capsuleHeight)
            return {
              error: `Filled height cannot exceed the capsule height of ${fmt(capsuleHeight, 3)} m (${fmt(L, 3)} m barrel + 2 × ${fmt(r, 3)} m end).`,
            };
          filled = capsuleFilledVolume(r, L, fh);
        } else {
          filled = volume;
        }
      }
      if (!(volume > 0)) return { error: 'Invalid dimensions, volume must be positive.' };
      return {
        outputs: [
          { label: 'Total volume', value: fmt(volume, 3), unit: 'm³', flag: 'key' },
          { label: 'Total volume', value: fmt(volume * 1000, 0), unit: 'L' },
          { label: 'Filled volume', value: fmt(filled * 1000, 0), unit: 'L' },
          { label: 'Water weight', value: fmt(filled * 1000, 0), unit: 'kg' },
        ],
      };
    },
  },

  {
    id: 'coneTankVolume',
    group: 'Solids & Tanks',
    label: 'Cone-Bottom Tank',
    blurb: 'Accurate volume for a circular tank with a conical or frustum base.',
    method:
      'Cylinder = (πD²/4) × wall height. Cone frustum = (π × cone depth ÷ 12) × (D² + D·d + d²), d = outlet diameter. Total = cylinder + frustum.',
    fields: [
      { key: 'diameter', label: 'Cylinder diameter', unit: 'm', def: '6', hint: 'Internal diameter' },
      { key: 'wall', label: 'Straight wall height', unit: 'm', def: '1.2', hint: 'Vertical side height' },
      { key: 'coneDepth', label: 'Cone depth', unit: 'm', def: '0.6', hint: 'Apex to wall transition' },
      { key: 'outlet', label: 'Bottom outlet diameter', unit: 'm', def: '0.1', hint: '0 for a full cone' },
    ],
    compute: (v) => {
      const D = n(v.diameter);
      const wall = n(v.wall);
      const hc = n(v.coneDepth);
      const dOut = n(v.outlet);
      if (!(D > 0 && wall >= 0 && hc >= 0)) return { error: 'Enter valid tank dimensions.' };
      const dout = dOut >= 0 ? dOut : 0;
      const cyl = (Math.PI / 4) * D * D * wall;
      const cone = (Math.PI * hc / 12) * (D * D + D * dout + dout * dout);
      const total = cyl + cone;
      if (!(total > 0)) return { error: 'Volume must be positive.' };
      return {
        outputs: [
          { label: 'Cylinder volume', value: fmt(cyl, 3), unit: 'm³' },
          { label: 'Cone volume', value: fmt(cone, 3), unit: 'm³' },
          { label: 'Total volume', value: fmt(total, 3), unit: 'm³', flag: 'key' },
          { label: 'Total volume', value: fmt(total * 1000, 0), unit: 'L' },
        ],
      };
    },
  },

  {
    id: 'settlingBasin',
    group: 'Solids & Tanks',
    label: 'Settling Basin',
    blurb: 'Clarifier or radial-flow settler sized on surface overflow rate.',
    method:
      'Surface area = flow ÷ surface overflow rate. With an L:W ratio, W = √(A ÷ ratio), L = ratio × W. HRT = (A × depth) ÷ flow. ' +
      'The overflow-rate approach is preferred over sizing on retention time (Stechey & Trudell 1990, via Timmons & Ebeling Ch.5): ' +
      'for off-line basins, the usual RAS case, they recommend 40–80 m³/m²/day, i.e. 1.67–3.33 m/h.',
    fields: [
      { key: 'flow', label: 'Flow rate', unit: 'm³/h', def: '60', hint: 'Flow to the settler' },
      {
        key: 'sor',
        label: 'Surface overflow rate',
        unit: 'm/h',
        def: String(SOR_DESIGN_M_PER_H),
        hint:
          'Off-line basins, the usual RAS case: 1.67–3.33 m/h (40–80 m³/m²/day), Stechey & Trudell 1990 via Timmons & Ebeling Ch.5. ' +
          'Default 2.5 m/h is Mudrak 1981, who measured >90% settleable-solids removal at 60 m³/m²/day. ' +
          'Going lower buys little — the same source found no notable improvement when the loading rate was reduced by up to a factor of three. ' +
          'Other configurations load far higher: full-flow 14.3 m/h, quiescent zone 34.0 m/h (IDEQ 1998, Table 5.2).',
      },
      {
        key: 'depth',
        label: 'Side water depth',
        unit: 'm',
        def: '1.5',
        hint: 'For retention time. Ch.5 recommends a minimum of 1 m (Liao & Mayo 1974).',
      },
      {
        key: 'ratio',
        label: 'Length : width ratio',
        unit: '',
        def: '4',
        hint: 'Ch.5 reports recommended basin length:width from 4:1 to 8:1 (Arceivala 1983).',
      },
    ],
    compute: (v) => {
      const flow = n(v.flow);
      const sor = n(v.sor);
      const depth = n(v.depth);
      const ratio = n(v.ratio);
      if (!(flow > 0 && sor > 0)) return { error: 'Flow and overflow rate must be positive.' };
      const area = flow / sor;
      const [sorLo, sorHi] = SOR_BAND_M_PER_H;
      const outOfBand = sor < sorLo || sor > sorHi;
      const out = [
        { label: 'Required surface area', value: fmt(area, 2), unit: 'm²', flag: 'key' },
        {
          label: 'Overflow rate used',
          value: `${fmt(sor, 2)} m/h (${fmt(sor * 24, 0)} m³/m²/day)`,
          unit: '',
          flag: outOfBand ? 'over' : null,
        },
      ];
      if (ratio > 0) {
        const w = Math.sqrt(area / ratio);
        const l = ratio * w;
        out.push({ label: 'Basin width', value: fmt(w, 2), unit: 'm' });
        out.push({ label: 'Basin length', value: fmt(l, 2), unit: 'm' });
      }
      if (depth > 0) {
        const hrt = (area * depth) / flow;
        out.push({ label: 'Hydraulic retention', value: fmt(hrt * 60, 0), unit: 'min' });
      }
      if (outOfBand) {
        out.push({
          label: 'Outside the off-line design band',
          value:
            sor < sorLo
              ? `Below ${sorLo} m/h. This oversizes the basin — Timmons & Ebeling Ch.5 reports no notable gain in removal when the loading rate is reduced by up to a factor of three. Check you are not sizing an off-line basin on thickening-basin figures.`
              : `Above ${sorHi} m/h. Beyond the 40–80 m³/m²/day range for off-line basins. Full-flow (14.3 m/h) and quiescent-zone (34.0 m/h) configurations do load this high — confirm which you are designing.`,
          unit: '',
          flag: 'over',
        });
      }
      out.push({
        label: 'Note',
        value: 'Size for peak flow. Inlet and outlet weir design matters as much as area — visible currents in the basin defeat it regardless of floor area.',
        unit: '',
      });
      return { outputs: out };
    },
  },
];

const GROUP_ORDER = [
  'Water Quality',
  'Biofilter & Treatment',
  'Production',
  'Hydraulics',
  'Solids & Tanks',
];

/* ------------------------------------------------------------------ */
/* localStorage helpers                                                */
/* ------------------------------------------------------------------ */

const readProjects = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('aqua-iq-projects') || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readActiveProj = () => {
  try {
    const raw = localStorage.getItem('aqua-iq-active-proj');
    if (!raw) return null;
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object' && p.id) return String(p.id);
      return String(p);
    } catch {
      return String(raw);
    }
  } catch {
    return null;
  }
};

const readResults = () => {
  try {
    const p = JSON.parse(localStorage.getItem('aqua-iq-calc-results') || '{}');
    return p && typeof p === 'object' ? p : {};
  } catch {
    return {};
  }
};

const readWorksheets = () => {
  try {
    const raw = JSON.parse(localStorage.getItem('aqua-iq-calc-worksheet') || '{}');
    if (Array.isArray(raw)) return { __none__: raw };
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function AquaIQ_Calculator_Dashboard({ onSendToGia, pendingEdit, onEditLoaded } = {}) {
  /*
   * t() for the dashboard's own chrome, tc() for calculator text. tc() is
   * applied only on the way to the screen: CALCS, saved entries and the text
   * sent to Gia stay English, because the method fingerprints and Gia's
   * grounding are keyed on the English.
   */
  const { t, tc, num, uiLang, calcLang } = useI18n();
  // For effects that only fire on other changes: read the current t without
  // re-running them when the language changes.
  const tRef = useRef(t);
  tRef.current = t;
  const [activeCalc, setActiveCalc] = useState('stockingDensity');
  const [forms, setForms] = useState(() => {
    const init = {};
    CALCS.forEach((c) => {
      init[c.id] = {};
      c.fields.forEach((f) => {
        init[c.id][f.key] = f.def != null ? f.def : '';
      });
    });
    return init;
  });
  /*
   * THE WORKSHEET AND THE PROJECT IT BELONGS TO ARE ONE PIECE OF STATE.
   *
   * They used to be two: `worksheet` held the rows, and the effect that saved
   * them read the CURRENT activeProjId to decide where to put them. Those two
   * do not change in the same render, so every load wrote the outgoing
   * project's rows under the incoming project's key before correcting itself
   * one render later:
   *
   *   mount   rows are still the initial [] while activeProjId has become P,
   *           so P's stored sheet is overwritten with []
   *   switch  rows are still X's while activeProjId has become Y,
   *           so Y's stored sheet is overwritten with X's rows
   *
   * Both self-corrected on the following render, so they were invisible in
   * normal use, and permanent if the component came apart inside that window:
   * closing the tab, navigating away, switching out of the calculator view.
   * A member's calculator sheet quietly emptied is exactly the kind of loss
   * nobody can prove afterwards.
   *
   * Keeping the key WITH the rows makes the wrong write unrepresentable. The
   * save effect no longer asks which project is active, it writes what the
   * rows themselves say they are.
   */
  const [sheet, setSheet] = useState(() => {
    const all = readWorksheets();
    return { key: '__none__', rows: Array.isArray(all['__none__']) ? all['__none__'] : [] };
  });
  const worksheet = sheet.rows;
  // Same shape as a useState setter, value or updater, so every existing call
  // site is unchanged. The key rides along untouched.
  const setWorksheet = useCallback((next) => {
    setSheet((prev) => ({
      key: prev.key,
      rows: typeof next === 'function' ? next(prev.rows) : next,
    }));
  }, []);
  const [projects, setProjects] = useState([]);
  const [activeProjId, setActiveProjId] = useState(null);
  const [toast, setToast] = useState('');
  const [showMethod, setShowMethod] = useState(false);
  // Phone shows one pane at a time; 'form' or 'worksheet'.
  const [mobileTab, setMobileTab] = useState('form');
  /*
   * The saved entry an in-progress edit came from. A calculation run while this
   * is set records what it supersedes, so the pair reads as a revision.
   * Cleared when the user moves to a different calculator — at that point they
   * are no longer editing that result.
   */
  const [editSource, setEditSource] = useState(null);

  useEffect(() => {
    const projs = readProjects();
    setProjects(projs);
    const active = readActiveProj();
    if (active && projs.some((p) => String(p.id) === active)) {
      setActiveProjId(active);
    }
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // Load: the rows and the key they came from are set together.
  useEffect(() => {
    const all = readWorksheets();
    const k = activeProjId || '__none__';
    setSheet({ key: k, rows: Array.isArray(all[k]) ? all[k] : [] });
  }, [activeProjId]);

  // Save: to the key the rows belong to, never to whichever project happens to
  // be active at this instant. See the note on the sheet state above.
  useEffect(() => {
    try {
      const all = readWorksheets();
      all[sheet.key] = sheet.rows;
      localStorage.setItem('aqua-iq-calc-worksheet', JSON.stringify(all));
    } catch (e) {}
  }, [sheet]);

  /*
   * Breakpoints key off the width this component actually gets, not the
   * viewport. With the app sidebar open a 1024px viewport leaves the dashboard
   * about 614px, which wants the two-pane treatment, not three panes crushed
   * together. ResizeObserver where available, viewport width as the fallback.
   */
  const rootRef = useRef(null);
  const [cw, setCw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      const w = el ? Math.round(el.getBoundingClientRect().width) : 0;
      setCw(w > 0 ? w : window.innerWidth);
    };
    measure();
    /*
     * Measure on the frame AFTER the event. `resize` and `orientationchange`
     * both fire before the new layout is applied, so measuring synchronously
     * reads the width the element had a moment ago and the breakpoint lands one
     * resize behind. Two frames is belt and braces for orientation changes,
     * where the second reflow can trail the first.
     */
    let raf1 = 0;
    let raf2 = 0;
    const schedule = () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(measure);
      });
    };
    /*
     * Both a listener and an observer, deliberately. The listener is what
     * reliably fires on rotation and window resize; the observer adds the case
     * the listener misses — the app sidebar opening or closing, which changes
     * this component's width with no window event at all. Neither covers both.
     */
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && rootRef.current) {
      ro = new ResizeObserver(schedule);
      ro.observe(rootRef.current);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      if (ro) ro.disconnect();
    };
  }, []);

  /*
   * Load a saved calculation the user asked to edit. Recalculating from here
   * creates a fresh worksheet entry, so saving adds a second saved calculation
   * rather than overwriting the original — deliberate, since something may
   * already have been built to the original figure.
   */
  useEffect(() => {
    if (!pendingEdit || !pendingEdit.calcId || !pendingEdit.rawInputs) return;
    if (!CALCS.some((c) => c.id === pendingEdit.calcId)) {
      setToast(tRef.current('calc.toast.gone'));
      if (onEditLoaded) onEditLoaded();
      return;
    }
    setActiveCalc(pendingEdit.calcId);
    setEditSource(
      pendingEdit.id
        ? { id: pendingEdit.id, ts: pendingEdit.ts, calcLabel: pendingEdit.calcLabel, calcId: pendingEdit.calcId }
        : null
    );
    setForms((prev) => ({
      ...prev,
      [pendingEdit.calcId]: { ...prev[pendingEdit.calcId], ...pendingEdit.rawInputs },
    }));
    setMobileTab('form');
    setToast(tRef.current('calc.toast.loaded'));
    if (onEditLoaded) onEditLoaded();
  }, [pendingEdit, onEditLoaded]);

  const calc = useMemo(
    () => CALCS.find((c) => c.id === activeCalc) || CALCS[0],
    [activeCalc]
  );

  /*
   * Selecting a calculator drops any in-progress edit link, because the user is
   * no longer editing that saved result.
   *
   * Done here rather than in an effect on activeCalc: an effect sees the
   * pre-update activeCalc on its first pass, and under StrictMode's double
   * invocation it runs that stale comparison a second time and clears the link
   * that the pendingEdit effect had just set — so an edited result saved with
   * no reference to the entry it revises. Clearing on the actual user action is
   * deterministic and does not care how many times effects run.
   */
  const selectCalc = (id) => {
    setActiveCalc(id);
    setEditSource((src) => (src && src.calcId === id ? src : null));
  };

  const setField = (key, val) =>
    setForms((prev) => ({
      ...prev,
      [activeCalc]: { ...prev[activeCalc], [key]: val },
    }));

  /*
   * Three layouts, measured on the component's own width:
   *   phone   (<680)      one column, one pane at a time, sticky Calculate
   *   tablet  (680-1023)  nav collapsed to a selector, form + worksheet side by side
   *   desktop (>=1024)    the original three-pane layout, unchanged
   */
  const phone = cw < 680;
  const tablet = cw >= 680 && cw < 1024;
  const desktop = cw >= 1024;

  const runCalc = () => {
    const vals = forms[activeCalc] || {};
    // A value that could be read two ways a thousand apart ("5.000") is
    // refused with the reason, rather than computed from a guess.
    for (const f of calc.fields) {
      if (f.type === 'select' || resolveField(f.hidden, vals)) continue;
      const issue = numberInputIssue(vals[f.key]);
      if (issue) {
        setToast(t('calc.ambiguousNumber', {
          label: tc(resolveField(f.label, vals)),
          typed: issue.typed,
          // Plain digits, never grouped: the message tells the user what to
          // type, and "5,000" would be advice to type a separator.
          a: String(issue.asDecimal),
          b: String(issue.asThousands),
        }));
        return;
      }
    }
    const result = calc.compute(vals);
    if (result.error) {
      setToast(tc(result.error));
      return;
    }
    // The worksheet is a separate tab on a phone, so move to it — otherwise
    // pressing Calculate looks like it did nothing.
    if (phone) setMobileTab('worksheet');
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      calcId: calc.id,
      calcLabel: calc.label,
      ts: new Date().toLocaleString('en-GB'),
      // Method version this result was computed at, so a later correction to
      // the formula can flag this entry as superseded rather than leaving a
      // wrong number sitting in a project looking authoritative.
      calcVersion: getCalcVersion(calc.id),
      // Which rules read the typed numbers, so a result computed before the
      // "5.000" fix can be told apart from one computed after it.
      inputReader: INPUT_READER_VERSION,
      // Keyed form values, kept so the entry can be re-run in one click.
      // `inputs` below is display-shaped and loses the field keys.
      rawInputs: { ...vals },
      // Names the saved result this one revises, when it came from an Edit.
      ...(editSource && editSource.calcId === calc.id
        ? { supersedes: { id: editSource.id, ts: editSource.ts, calcLabel: editSource.calcLabel } }
        : {}),
      inputs: calc.fields
        .filter((f) => !resolveField(f.hidden, vals))
        .map((f) => {
          let val = vals[f.key];
          if (f.type === 'select') {
            const opt = f.options.find((o) => o.value === val);
            val = opt ? opt.text : val;
          }
          return {
            label: resolveField(f.label, vals),
            value: val,
            unit: resolveField(f.unit, vals),
          };
        })
        .filter((i) => i.value !== '' && i.value != null),
      outputs: result.outputs,
      saved: false,
    };
    setWorksheet((prev) => [entry, ...prev]);
  };

  const removeEntry = (id) =>
    setWorksheet((prev) => prev.filter((e) => e.id !== id));

  const clearWorksheet = () => setWorksheet([]);

  /*
   * Load a saved entry's inputs back into its calculator so the user can
   * recompute it under the current method. Only possible for entries stamped
   * with rawInputs — entries saved before stamping existed carry only display
   * labels, which no longer map cleanly to field keys now that some labels are
   * dynamic. Those have to be re-entered by hand.
   */
  const reRunEntry = (entry) => {
    const target = CALCS.find((c) => c.id === entry.calcId);
    if (!target) {
      setToast(t('calc.toast.gone'));
      return;
    }
    if (!entry.rawInputs) {
      selectCalc(entry.calcId);
      setToast(t('calc.toast.predates'));
      return;
    }
    setForms((prev) => ({
      ...prev,
      [entry.calcId]: { ...prev[entry.calcId], ...entry.rawInputs },
    }));
    selectCalc(entry.calcId);
    setToast(t('calc.toast.inputsLoaded'));
  };

  const saveToProject = (entry) => {
    if (!activeProjId) {
      setToast(t('calc.toast.noProject'));
      return;
    }
    const store = readResults();
    const list = Array.isArray(store[activeProjId]) ? store[activeProjId] : [];
    const payload = {
      id: entry.id,
      calcId: entry.calcId,
      calcLabel: entry.calcLabel,
      ts: entry.ts,
      savedAt: Date.now(),
      calcVersion: entry.calcVersion,
      inputReader: entry.inputReader,
      rawInputs: entry.rawInputs,
      supersedes: entry.supersedes,
      inputs: entry.inputs,
      outputs: entry.outputs,
    };
    store[activeProjId] = [payload, ...list.filter((r) => r.id !== entry.id)];
    try {
      localStorage.setItem('aqua-iq-calc-results', JSON.stringify(store));
      setWorksheet((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, saved: true } : e))
      );
      const proj = projects.find((p) => String(p.id) === activeProjId);
      setToast(proj ? t('calc.toast.savedTo', { name: proj.name || proj.id }) : t('calc.toast.savedToProject'));
    } catch {
      setToast(t('calc.toast.writeFailed'));
    }
  };

  /* ----------------------------- styles ----------------------------- */

  const narrow = phone || tablet;

  // 44px is the minimum comfortable touch target. Applied on touch layouts
  // only, so the desktop three-pane layout stays exactly as it was.
  const TOUCH = 44;
  const SAFE_L = 'max(16px, env(safe-area-inset-left))';
  const SAFE_R = 'max(16px, env(safe-area-inset-right))';
  const SAFE_B = 'max(16px, env(safe-area-inset-bottom))';
  const LINE = 'var(--border)';
  const SOFT = 'var(--bg)';          // inset input / recessed panel background
  const TEAL_TINT = 'var(--accent-bg)';
  const TEAL_LIGHT = 'var(--accent-bg)';
  const SHADOW = '0 1px 2px rgba(16,40,37,0.05), 0 10px 28px rgba(16,40,37,0.06)';
  const FONT = 'inherit';

  const S = {
    root: {
      display: 'flex',
      flexDirection: desktop ? 'row' : 'column',
      height: desktop ? '76vh' : 'auto',
      minHeight: desktop ? 540 : 'auto',
      width: '100%',
      minWidth: 0,
      background: 'var(--card)',
      color: 'var(--text)',
      fontFamily: FONT,
      fontSize: 14,
      borderRadius: desktop ? 18 : 0,
      overflow: desktop ? 'hidden' : 'visible',
      WebkitFontSmoothing: 'antialiased',
    },
    // Desktop keeps the permanent category list. Touch layouts collapse it
    // into a native select, which gets a real picker and a 44px target for free.
    nav: { width: 204, flexShrink: 0, borderRight: '1px solid ' + LINE,
           background: 'var(--sidebar)', overflowY: 'auto', padding: '18px 0' },
    topBar: {
      /*
       * Deliberately not sticky. The app's own header is already sticky at the
       * document level while this sits inside a separately-scrolling pane, so
       * a second sticky bar either hides under the header or reserves dead
       * space above itself depending on which container is scrolling. Only the
       * Calculate button needs to stay reachable, and it lives in its own pane.
       */
      background: 'var(--card)',
      borderBottom: '1px solid ' + LINE,
      display: 'flex', flexDirection: 'column', gap: 9,
      paddingTop: 10, paddingBottom: 10,
      paddingLeft: SAFE_L, paddingRight: SAFE_R,
    },
    navSelect: {
      width: '100%', boxSizing: 'border-box', minHeight: TOUCH,
      padding: '10px 12px', border: '1px solid ' + LINE, borderRadius: 11,
      // >=16px or iOS Safari zooms the page when the control takes focus.
      fontSize: 16, fontWeight: 600, color: 'var(--text)', background: 'var(--card)',
      fontFamily: FONT, cursor: 'pointer',
    },
    tabRow: { display: 'flex', gap: 8 },
    tabBtn: (active) => ({
      flex: 1, minHeight: TOUCH, padding: '10px 12px',
      border: '1px solid ' + (active ? 'var(--accent-fill)' : LINE),
      background: active ? 'var(--accent-fill)' : 'var(--card)',
      color: active ? '#fff' : 'var(--text)',
      borderRadius: 11, fontSize: 15, fontWeight: 700,
      fontFamily: FONT, cursor: 'pointer',
    }),
    /*
     * display:contents on desktop so nav / form / worksheet stay direct flex
     * children of root and the original three-pane layout is untouched.
     * Tablet makes this a two-pane row; phone shows one pane at a time.
     * Height in dvh, never vh: the Android Chrome address bar makes vh wrong.
     */
    panes: desktop
      ? { display: 'contents' }
      : tablet
        ? { display: 'flex', flexDirection: 'row', minWidth: 0,
            /*
             * dvh so the Android address bar collapsing does not leave a gap.
             * The 120px subtracted is the app header plus this component's own
             * selector bar; both are fixed-height, so the panes fill what is
             * left rather than stopping short and showing page background.
             */
            height: 'calc(100dvh - 120px)', minHeight: 420 }
        : { display: 'block', minWidth: 0 },
    navGroup: {
      padding: narrow ? '8px 18px 4px' : '6px 20px 6px',
      fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: MUTED, fontWeight: 700, marginTop: narrow ? 4 : 10,
    },
    navItem: (active) => ({
      padding: '9px 14px', margin: '2px 12px', borderRadius: 9,
      minHeight: narrow ? TOUCH : undefined,
      display: narrow ? 'flex' : undefined, alignItems: narrow ? 'center' : undefined,
      cursor: 'pointer', fontWeight: active ? 700 : 500,
      color: active ? '#ffffff' : 'var(--text)',
      background: active ? 'var(--accent-fill)' : 'transparent',
      lineHeight: 1.3, fontSize: 13.5, transition: 'background 140ms ease',
    }),
    centre: desktop
      ? { width: 352, flexShrink: 0, borderRight: '1px solid ' + LINE,
          padding: '28px 28px 32px', position: 'sticky', top: 0,
          alignSelf: 'flex-start', background: 'var(--card)', maxHeight: '100%',
          overflowY: 'auto' }
      : tablet
        ? { flex: '0 0 46%', maxWidth: '46%', minWidth: 0,
            borderRight: '1px solid ' + LINE, overflowY: 'auto',
            padding: '20px 20px 24px', background: 'var(--card)',
            WebkitOverflowScrolling: 'touch' }
        : { width: '100%', minWidth: 0, background: 'var(--card)',
            paddingTop: 18, paddingBottom: 0,
            paddingLeft: SAFE_L, paddingRight: SAFE_R },
    title: { fontSize: 20, fontWeight: 800, margin: '0 0 5px', letterSpacing: '-0.01em' },
    blurb: { fontSize: 13, color: MUTED, margin: '0 0 20px', lineHeight: 1.55 },
    label: { display: 'block', fontSize: 12.5, fontWeight: 600, margin: '0 0 6px', color: 'var(--text)' },
    field: { marginBottom: 15 },
    input: {
      width: '100%', boxSizing: 'border-box', padding: narrow ? '12px 13px' : '11px 13px',
      minHeight: narrow ? TOUCH : undefined,
      border: '1px solid ' + LINE, borderRadius: 11,
      // 16px is the floor on touch: below it iOS Safari zooms the page on focus.
      fontSize: narrow ? 16 : 14.5,
      color: 'var(--text)', background: SOFT,
      fontFamily: FONT, transition: 'border-color 140ms ease, background 140ms ease',
    },
    unit: { fontSize: 11.5, color: MUTED, marginLeft: 6, fontWeight: 500 },
    hint: { fontSize: 11.5, color: MUTED, margin: '5px 2px 0', lineHeight: 1.45 },
    btn: {
      width: '100%', marginTop: phone ? 0 : 10, padding: '13px 16px',
      minHeight: narrow ? TOUCH : undefined, background: 'var(--btn-bg)',
      color: 'var(--btn-text)', border: 'none', borderRadius: 999, fontSize: narrow ? 16 : 15,
      fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em',
      boxShadow: 'none', transition: 'transform 120ms ease',
    },
    /*
     * On a phone the inputs can run past a screen height, so Calculate sticks
     * to the bottom of the viewport rather than sitting below the last field.
     * Negative margins bleed it to the pane edges; the safe-area inset keeps
     * it clear of the home indicator.
     */
    btnBar: phone
      ? { position: 'sticky', bottom: 0, zIndex: 5,
          marginTop: 14, marginLeft: `calc(-1 * ${SAFE_L})`, marginRight: `calc(-1 * ${SAFE_R})`,
          paddingTop: 10, paddingLeft: SAFE_L,
          // Clear the fixed 48px calculator FAB sitting bottom-right.
          paddingRight: `calc(${SAFE_R} + 56px)`,
          paddingBottom: `calc(10px + ${SAFE_B})`,
          background: 'var(--footer-bg)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          borderTop: '1px solid ' + LINE }
      : {},
    methodWrap: { marginTop: 20, borderTop: '1px solid ' + LINE, paddingTop: 14 },
    methodToggle: {
      background: 'none', border: 'none', color: TEAL_DARK,
      padding: narrow ? '10px 0' : 0,
      minHeight: narrow ? TOUCH : undefined,
      display: narrow ? 'flex' : undefined, alignItems: narrow ? 'center' : undefined,
      fontSize: narrow ? 14 : 12.5, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em',
    },
    methodBox: {
      marginTop: 12, background: TEAL_TINT, border: '1px solid ' + LINE,
      borderRadius: 12, padding: '13px 15px',
    },
    methodLabel: {
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: MUTED, margin: '0 0 5px',
    },
    methodText: {
      fontSize: 12.5, lineHeight: 1.6, color: 'var(--text)', margin: '0 0 6px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    },
    disclaimer: {
      marginTop: 14, background: 'var(--warn-bg)', border: '1px solid var(--warn-border)',
      borderRadius: 12, padding: '12px 14px', fontSize: 11.5,
      lineHeight: 1.55, color: 'var(--warn-text)',
    },
    panelDisclaimer: {
      background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 12,
      padding: '11px 14px', fontSize: 11.5, lineHeight: 1.55,
      color: 'var(--warn-text)', marginBottom: 18,
    },
    panel: desktop
      ? { flex: 1, minWidth: 0, wordBreak: 'break-word', overflowY: 'auto',
          padding: '26px 30px 48px', background: SOFT }
      : tablet
        ? { flex: 1, minWidth: 0, wordBreak: 'break-word', overflowY: 'auto',
            padding: '20px 20px 32px', background: SOFT,
            WebkitOverflowScrolling: 'touch' }
        : { width: '100%', minWidth: 0, wordBreak: 'break-word', background: SOFT,
            paddingTop: 18, paddingBottom: `calc(24px + ${SAFE_B})`,
            paddingLeft: SAFE_L, paddingRight: SAFE_R },
    panelHead: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, marginBottom: 18, flexWrap: 'wrap',
    },
    panelTitle: { fontSize: 16, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' },
    select: {
      padding: '9px 12px', border: '1px solid ' + LINE, borderRadius: 10,
      minHeight: narrow ? TOUCH : undefined,
      fontSize: narrow ? 16 : 13, background: 'var(--card)', color: 'var(--text)', fontFamily: FONT,
      cursor: 'pointer',
    },
    card: {
      background: 'var(--card)', border: '1px solid ' + LINE, borderRadius: 14,
      padding: '16px 18px', marginBottom: 14, boxShadow: SHADOW,
    },
    cardHead: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: 10, marginBottom: 11,
    },
    cardTitle: { fontSize: 15, fontWeight: 800, color: TEAL_DARK, letterSpacing: '-0.01em' },
    ts: { fontSize: 11, color: MUTED },
    inputsRow: { fontSize: 12, color: MUTED, marginBottom: 13, lineHeight: 1.65 },
    // Amber rather than the red 'over' treatment: the result is superseded,
    // not out of range, and the two shouldn't read as the same thing.
    staleBox: {
      background: 'var(--stale-bg)', border: '1px solid var(--stale-border)', borderRadius: 10,
      padding: '10px 12px', marginBottom: 12, fontSize: 11.5,
      lineHeight: 1.55, color: 'var(--stale-text)',
    },
    rerunBtn: {
      padding: '9px 13px', minHeight: narrow ? TOUCH : undefined,
      background: 'var(--card)', color: 'var(--stale-text)',
      border: '1px solid var(--stale-border)', borderRadius: 10, fontSize: 13,
      fontWeight: 700, cursor: 'pointer',
    },
    outRow: (key, over) => ({
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '8px 12px', borderRadius: 9, marginBottom: 6,
      background: over ? 'var(--danger-bg)' : key ? TEAL_LIGHT : 'var(--calc-row)',
      fontWeight: key ? 700 : 500,
    }),
    proseRow: (over) => ({
      padding: '8px 12px', borderRadius: 9, marginBottom: 6,
      background: over ? 'var(--danger-bg)' : 'var(--calc-row)',
    }),
    proseLabel: {
      fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em',
      color: MUTED, fontWeight: 700, marginBottom: 4,
    },
    proseText: { fontWeight: 400, fontSize: 13, lineHeight: 1.5, color: 'var(--text)' },
    outVal: (over) => ({
      fontVariantNumeric: 'tabular-nums',
      color: over ? 'var(--danger-text)' : 'var(--text)', textAlign: 'right',
      // The panel sets word-break: break-word, which lets a squeezed value
      // split mid-unit ("kg/da" / "y") beside a long label, as Spanish labels
      // are. The value keeps whole words and yields at most 60% of the row;
      // the label wraps instead.
      wordBreak: 'normal', overflowWrap: 'normal', flexShrink: 0, maxWidth: '60%',
    }),
    outLabel: { minWidth: 0 },
    cardBtns: { display: 'flex', gap: 8, marginTop: 12 },
    saveBtn: (saved) => ({
      flex: 1, padding: '9px 13px', minHeight: narrow ? TOUCH : undefined,
      background: saved ? TEAL_LIGHT : 'var(--btn-bg)',
      color: saved ? TEAL_DARK : 'var(--btn-text)',
      border: saved ? '1px solid ' + TEAL : 'none',
      borderRadius: 999, fontSize: 13, fontWeight: 700,
      cursor: saved ? 'default' : 'pointer',
    }),
    rmBtn: {
      padding: '9px 13px', minHeight: narrow ? TOUCH : undefined,
      background: 'var(--card)', color: MUTED,
      border: '1px solid ' + LINE, borderRadius: 999, fontSize: 13,
      fontWeight: 600, cursor: 'pointer',
    },
    giaBtn: {
      flex: 1, padding: '9px 13px', minHeight: narrow ? TOUCH : undefined,
      background: 'var(--card)', color: TEAL_DARK,
      border: '1px solid ' + TEAL, borderRadius: 999, fontSize: 13,
      fontWeight: 700, cursor: 'pointer',
    },
    empty: {
      textAlign: 'center', color: MUTED, fontSize: 13.5,
      padding: '64px 22px', lineHeight: 1.65,
    },
    toast: {
      position: 'fixed', bottom: 26, left: '50%',
      transform: 'translateX(-50%)', background: 'var(--toast-bg)', color: 'var(--toast-text)',
      padding: '12px 22px', borderRadius: 12, fontSize: 13, fontWeight: 600,
      zIndex: 9999, maxWidth: '86vw',
      boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
    },
  };

  /* ----------------------------- render ----------------------------- */

  return (
    <div className="calc-dashboard" style={S.root} ref={rootRef}>
      {desktop ? (
        <nav style={S.nav}>
          {GROUP_ORDER.map((g) => (
            <div key={g}>
              <div style={S.navGroup}>{tc(g)}</div>
              {CALCS.filter((c) => c.group === g).map((c) => (
                <div
                  key={c.id}
                  style={S.navItem(c.id === activeCalc)}
                  onClick={() => selectCalc(c.id)}
                >
                  {tc(c.label)}
                </div>
              ))}
            </div>
          ))}
        </nav>
      ) : (
        <div style={S.topBar}>
          <select
            style={S.navSelect}
            value={activeCalc}
            onChange={(e) => selectCalc(e.target.value)}
            aria-label={t('calc.choose')}
          >
            {GROUP_ORDER.map((g) => (
              <optgroup key={g} label={tc(g)}>
                {CALCS.filter((c) => c.group === g).map((c) => (
                  <option key={c.id} value={c.id}>{tc(c.label)}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {phone && (
            <div style={S.tabRow} role="tablist">
              <button
                style={S.tabBtn(mobileTab === 'form')}
                onClick={() => setMobileTab('form')}
                role="tab"
                aria-selected={mobileTab === 'form'}
              >
                {t('calc.tabCalculator')}
              </button>
              <button
                style={S.tabBtn(mobileTab === 'worksheet')}
                onClick={() => setMobileTab('worksheet')}
                role="tab"
                aria-selected={mobileTab === 'worksheet'}
              >
                {t('calc.tabWorksheet')}{worksheet.length ? ` (${worksheet.length})` : ''}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={S.panes}>
      {(!phone || mobileTab === 'form') && (
      <section style={S.centre}>
        <h2 style={S.title}>{tc(calc.label)}</h2>
        <p style={S.blurb}>{tc(calc.blurb)}</p>

        {calc.fields.map((f) => {
          const fVals = forms[activeCalc];
          if (resolveField(f.hidden, fVals)) return null;
          const fLabel = resolveField(f.label, fVals);
          const fUnit = resolveField(f.unit, fVals);
          const fHint = resolveField(f.hint, fVals);
          return (
          <div key={f.key} style={S.field}>
            <label style={S.label}>
              {tc(fLabel)}
              {fUnit ? <span style={S.unit}>{fUnit}</span> : null}
            </label>
            {f.type === 'select' ? (
              <select
                style={S.input}
                value={forms[activeCalc][f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              >
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {tc(o.text)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                style={S.input}
                /*
                 * text + inputMode="decimal", not type="number": number brings
                 * spinner arrows, and in comma-decimal locales it can hand back
                 * a value that parseFloat silently truncates. n() resolves both
                 * separators instead.
                 */
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder={tc(resolveField(f.placeholder, fVals)) || undefined}
                value={forms[activeCalc][f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runCalc();
                }}
              />
            )}
            {fHint ? <div style={S.hint}>{tc(fHint)}</div> : null}
          </div>
          );
        })}

        <div style={S.btnBar}>
          <button
            style={S.btn}
            onClick={runCalc}
            onMouseDown={(e) => e.preventDefault()}
          >
            {t('calc.calculate')}
          </button>
        </div>
        {/* Page-level estimates note, styled like the AI line under the chat box. */}
        <p className="calc-estimate-note calc-estimate-page" lang="en">
          Results are estimates for planning only. Have a qualified engineer check your design before you buy or build.
        </p>

        <div style={S.methodWrap}>
          <button
            style={S.methodToggle}
            onClick={() => setShowMethod((s) => !s)}
          >
            {showMethod ? '▾ ' : '▸ '}{t('calc.method')}
          </button>
          {showMethod ? (
            <div style={S.methodBox}>
              <p style={S.methodLabel}>{t('calc.method')}</p>
              <p style={S.methodText}>{tc(calc.method)}</p>
            </div>
          ) : null}
          <div style={S.disclaimer} lang="en">
            Indicative estimate only. These calculators are a general
            engineering aid, not a substitute for professional design.
            AquaNetica Group Ltd accepts no liability for decisions made using
            these results.
            Verify all figures independently and confirm with a qualified
            aquaculture or engineering professional before relying on them.
          </div>
          {uiLang !== 'en' && <div style={S.disclaimer} lang={uiLang}>{t('legal.englishGoverning')}</div>}
        </div>
      </section>
      )}

      {(!phone || mobileTab === 'worksheet') && (
      <div style={S.panel}>
        <div style={S.panelDisclaimer} lang="en">
          Indicative estimates only. Not professional design advice.
          AquaNetica Group Ltd accepts no liability for use of these results. Verify
          independently and confirm with a qualified professional before
          relying on any figure.
          {uiLang !== 'en' && <span lang={uiLang} style={{ display: 'block', marginTop: 4 }}>{t('legal.englishGoverning')}</span>}
        </div>
        <div style={S.panelHead}>
          <h3 style={S.panelTitle}>
            {t('calc.tabWorksheet')}{worksheet.length ? ` (${worksheet.length})` : ''}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              style={S.select}
              value={activeProjId || ''}
              onChange={(e) => setActiveProjId(e.target.value || null)}
            >
              <option value="">{projects.length ? t('calc.noProjectRun') : t('calc.noProjectsYet')}</option>
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name || t('calc.projectN', { id: p.id })}
                </option>
              ))}
            </select>
            {worksheet.length > 0 && (
              <button style={S.rmBtn} onClick={clearWorksheet}>
                {t('calc.clearAll')}
              </button>
            )}
          </div>
        </div>

        {worksheet.length === 0 ? (
          <div style={S.empty}>
            {t('calc.emptyTitle')}
            <br />
            {t('calc.emptyBody')}
          </div>
        ) : (
          worksheet.map((e) => (
            <div key={e.id} style={S.card}>
              <div style={S.cardHead}>
                <span style={S.cardTitle}>{tc(e.calcLabel)}</span>
                <span style={S.ts}>{e.ts}</span>
              </div>
              {isEntryStale(e) && (
                <div style={S.staleBox}>
                  <strong>{isMethodStale(e) ? t('calc.methodChanged') : t('calc.misreadNumber')}</strong>
                  {staleNotes(e).map((note, idx) => (
                    <div key={idx} style={{ marginTop: 4 }}>{tc(note)}</div>
                  ))}
                  {misreadValues(e) ? (
                    <div style={{ marginTop: 4 }}>{t('calc.misreadNote', { values: misreadValues(e) })}</div>
                  ) : null}
                  <div style={{ marginTop: 4 }}>
                    {t('calc.rerunBefore')}
                  </div>
                </div>
              )}
              <div style={S.inputsRow}>
                {e.inputs.map((i, idx) => (
                  <span key={idx}>
                    {tc(i.label)}: <strong>{num(tc(i.value))}</strong>
                    {i.unit ? ` ${i.unit}` : ''}
                    {idx < e.inputs.length - 1 ? '  ·  ' : ''}
                  </span>
                ))}
              </div>
              {e.outputs.map((o, idx) => {
                // Same rule as the project view, from the shared module, so a
                // saved calculation lays out identically in both places.
                if (isProseOutput(o)) {
                  return (
                    <div key={idx} style={S.proseRow(o.flag === 'over')}>
                      <div style={S.proseLabel}>{tc(o.label)}</div>
                      <div style={S.proseText}>{num(tc(o.value))}</div>
                    </div>
                  );
                }
                return (
                  <div
                    key={idx}
                    style={S.outRow(o.flag === 'key', o.flag === 'over')}
                  >
                    <span style={S.outLabel}>
                      {tc(o.label)}
                      {o.flag === 'over' ? `  ${t('calc.check')}` : ''}
                    </span>
                    <span style={S.outVal(o.flag === 'over')}>
                      {num(tc(o.value))}{o.unit ? ` ${o.unit}` : ''}
                    </span>
                  </div>
                );
              })}
              <p className="calc-estimate-note" lang="en">
                Results are estimates for planning only. Have a qualified engineer check your design before you buy or build.
              </p>
              <div style={S.cardBtns}>
                {isEntryStale(e) && (
                  <button style={S.rerunBtn} onClick={() => reRunEntry(e)}>
                    {t('calc.rerun')}
                  </button>
                )}
                <button
                  style={S.saveBtn(e.saved)}
                  onClick={() => !e.saved && saveToProject(e)}
                  disabled={e.saved}
                >
                  {e.saved ? t('calc.saved') : t('calc.save')}
                </button>
                {onSendToGia ? (
                  <button
                    style={S.giaBtn}
                    onClick={() => {
                      const lines = [];
                      lines.push(`${e.calcLabel} (calculator result)`);
                      lines.push(
                        'Inputs: ' +
                          e.inputs
                            .map(
                              (i) =>
                                `${i.label} ${i.value}${i.unit ? ' ' + i.unit : ''}`
                            )
                            .join(', ')
                      );
                      lines.push(
                        'Results: ' +
                          e.outputs
                            .map(
                              (o) =>
                                `${o.label} ${o.value}${o.unit ? ' ' + o.unit : ''}`
                            )
                            .join(', ')
                      );
                      lines.push(
                        'Please sanity-check this against RAS engineering norms and flag anything off.'
                      );
                      onSendToGia(lines.join('\n'));
                      setToast(t('calc.toast.sent'));
                    }}
                  >
                    {t('calc.sendToGia')}
                  </button>
                ) : null}
                <button style={S.rmBtn} onClick={() => removeEntry(e.id)}>
                  {t('calc.remove')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      )}
      </div>

      {toast ? <div style={S.toast}>{toast}</div> : null}
    </div>
  );
}
